// REST client for the API Gateway backend (PLAN.md §17, §19, §20).
// All calls except login carry the session JWT.

import type {
  CacheKind,
  ChatMessage,
  ChatSession,
  ConversationSearchResponse,
  MedicalEvent,
  ModelConfig,
  Patient,
  Regimen,
  SearchScope,
  MedVariantOption,
  MedClarificationContext,
  FeedbackItem,
  FeedbackRating,
  FeedbackCategory,
  FeedbackStatus,
  FeedbackSnapshot,
  FeedbackUsageTurn,
  RatingEventType,
  NeedsRatingItem,
  FoodItemBreakdown,
  FoodNutritionPer100g,
  FoodCatalogView,
  UserFoodItem,
  NutrientTotals,
  NutrientReport,
  NutrientGap,
  MealType,
  BodyCompositionPayload,
  BodyMetric,
  BodyPhotoAngle,
  UserGymExercise,
  GymExerciseView,
  GymMetric,
  MuscleGroup,
  AnatomicalMuscle,
  GymRecommendationResult,
  GymProgramIntake,
  WorkoutTodayOptions,
  WorkoutTodayResult,
  BackfillSlotsResponse,
  BackfillLogEntry,
  CatchUpResponse,
  CatchUpLogEntry,
  CatchUpLogResult,
  TreatmentEffect,
  SideEffectSymptomMatch,
  Condition,
  TrackedSymptom,
  SymptomIdentity,
  DescribeExerciseResult,
  DeployedExerciseVisualContent,
  GymHowTo,
  GlobalExerciseRecord,
  CommunityConfig,
  CommunityIndexEntry,
  ResearchResult,
  CommunityTaxonomy,
  ExerciseContentBatch,
  ExerciseContentWorkflow,
  ExerciseReviewBulkResult,
  ExerciseReviewDecision,
  ExerciseReviewStatus,
  PainBodyMap,
  PainCorrelation,
  PatientExportEnvelope,
  BarcodeProduct,
  FluidType,
  BodyMeasurementPayload,
  MeasurementConditions,
  MeasurementDisplayUnit,
  CustomMeasurementSite,
  ChatDepth,
  PremiumDepth,
} from "@medassist/shared";
import { getToken, setToken, clearToken, getCognitoTokens } from "./auth.js";
import { refreshTokens as cognitoRefreshTokens, isCognitoConfigured } from "./cognito.js";
import { debugLog, recordBedrockInteraction } from "./debug.js";
import { recordTiming } from "./perf.js";
import { cachedRead, clearReadCache } from "./cache.js";

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, "");
const STREAM_URL = (import.meta.env.VITE_CHAT_STREAM_URL as string | undefined)?.replace(/\/+$/, "");

// Read-cache TTLs (PERFORMANCE_PLAN.md #5). Deliberately short: any write clears the
// cache, so these windows only bound staleness from changes this client didn't make
// (another household member, a finished import). `events` is the hot navigation path;
// `list` covers slower-changing collections.
const CACHE_TTL = { events: 15_000, list: 30_000 } as const;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly serverStack?: string
  ) {
    super(message);
  }
}

/** Options for the public {@link call} wrapper. */
interface CallOptions {
  /** When set on a GET, serve from the read cache within this TTL (PERFORMANCE_PLAN.md #5). */
  cacheTtlMs?: number;
  /** Abort the in-flight request (user cancelled). Rejects with an AbortError, not
   *  an ApiError — callers detect it with {@link isAbortError} and stay silent. */
  signal?: AbortSignal;
}

/** True for the rejection produced by aborting a fetch via an AbortSignal. A
 *  user-initiated cancel is not an error to report — callers use this to tell the
 *  two apart. */
export function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === "AbortError";
}

/**
 * Public API caller. A GET with `cacheTtlMs` is served from the short-lived read
 * cache (with concurrent-request coalescing); any successful write clears that cache
 * so reads never go stale. Everything else passes straight through to {@link callInner}.
 */
async function call<T>(
  path: string,
  method: string,
  body?: unknown,
  auth = true,
  opts?: CallOptions,
): Promise<T> {
  if (method === "GET" && opts?.cacheTtlMs) {
    return cachedRead(path, opts.cacheTtlMs, () => callInner<T>(path, method, body, auth, opts?.signal));
  }
  const result = await callInner<T>(path, method, body, auth, opts?.signal);
  // Any successful mutation invalidates all cached reads (simple + always correct).
  if (method !== "GET") clearReadCache();
  return result;
}

async function callInner<T>(path: string, method: string, body?: unknown, auth = true, signal?: AbortSignal): Promise<T> {
  if (!BASE) throw new ApiError("VITE_API_URL is not configured", 0);
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth) {
    const token = getToken();
    if (!token) throw new ApiError("Not signed in", 401);
    headers.Authorization = `Bearer ${token}`;
  }

  const started = performance.now();
  debugLog("info", `→ ${method} ${path}`);

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (networkErr) {
    // A user-initiated cancel also rejects here — pass it through untouched so
    // callers can tell it apart from a real network failure.
    if (isAbortError(networkErr)) {
      debugLog("info", `⊘ ${method} ${path} — cancelled`);
      throw networkErr;
    }
    // "Failed to fetch" lands here — network/CORS/preflight failures.
    const msg = networkErr instanceof Error ? networkErr.message : String(networkErr);
    debugLog("error", `✗ ${method} ${path} — network error: ${msg}`, {
      url: `${BASE}${path}`,
      hint: "Network/CORS/preflight failure — the request never got an HTTP response.",
    });
    throw new ApiError(`Network error calling ${path}: ${msg}`, 0);
  }

  const ms = Math.round(performance.now() - started);
  // Aggregate by method + path (query stripped) so repeated calls roll up into one
  // reviewable row (PERFORMANCE_PLAN.md — measurement).
  recordTiming(`API ${method} ${path.split("?")[0]}`, ms);

  if (res.status === 401) {
    // Phase-3: attempt a silent refresh via Cognito refresh token, then retry once.
    if (isCognitoConfigured()) {
      const tokens = getCognitoTokens();
      if (tokens) {
        try {
          const ok = await cognitoRefreshTokens();
          if (ok) {
          // Tokens have been updated in localStorage; read the new access token.
          const newTokens = getCognitoTokens();
          if (newTokens) {
          // Retry with the ID token (which carries cognito:groups for authz).
          const retryHeaders: Record<string, string> = { "Content-Type": "application/json", Authorization: `Bearer ${newTokens.idToken}` };
          const retry = await fetch(`${BASE}${path}`, { method, headers: retryHeaders, body: body === undefined ? undefined : JSON.stringify(body), signal });
          if (!retry.ok && retry.status === 401) {
            clearToken();
            debugLog("warn", `✗ ${method} ${path} — 401 after refresh, signing out`);
            throw new ApiError("Session expired — please sign in again", 401);
          }
          if (!retry.ok) {
            const d2 = (await retry.json().catch(() => ({}))) as Record<string, unknown>;
            throw new ApiError((d2.error as string) ?? `Request failed (${retry.status})`, retry.status);
          }
          return (await retry.json().catch(() => ({}))) as T;
          } // end if newTokens
          } // end if ok
        } catch (refreshErr) {
          if (refreshErr instanceof ApiError) throw refreshErr;
          // A cancel during the retry must not be mistaken for a failed refresh —
          // signing the user out here would be a very unwelcome surprise.
          if (isAbortError(refreshErr)) throw refreshErr;
          clearToken();
          debugLog("warn", `✗ ${method} ${path} — refresh failed, signing out`);
          throw new ApiError("Session expired — please sign in again", 401);
        }
      }
    }
    clearToken();
    debugLog("warn", `✗ ${method} ${path} — 401 (${ms}ms), signing out`);
    throw new ApiError("Session expired — please sign in again", 401);
  }

  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const message = (data.error as string) ?? `Request failed (${res.status})`;
    const detail = data.detail as string | undefined;
    const stack = data.stack as string | undefined;
    debugLog("error", `✗ ${method} ${path} — ${res.status} (${ms}ms): ${detail ?? message}`, stack ?? data);
    throw new ApiError(detail ? `${message}: ${detail}` : message, res.status, stack);
  }

  debugLog("info", `✓ ${method} ${path} — ${res.status} (${ms}ms)`);
  return data as T;
}

// ---- Auth ----
export async function login(username: string, password: string): Promise<void> {
  const { token } = await call<{ token: string }>("/login", "POST", { username, password }, false);
  setToken(token);
}

// ---- Patients ----
export async function getPatients(): Promise<Patient[]> {
  return (await call<{ patients: Patient[] }>("/patients", "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list })).patients;
}

export async function addPatient(firstName: string, lastName?: string): Promise<Patient> {
  return (await call<{ patient: Patient }>("/patients", "POST", { firstName, lastName })).patient;
}

// ---- Patient preferences (day-part times, §19.8, D-47) ----
/** Patch one or more patient preferences. Only the fields present in `prefs` are
 *  sent (and updated server-side) — omit a field to leave it untouched, so the
 *  Water page can set just the goal without disturbing day-part times. */
export interface PreferencesPatch {
  dayPartTimes?: Record<string, string>;
  interactionAlertLevels?: import("@medassist/shared").InteractionSeverity[];
  sideEffectAlertsEnabled?: boolean;
  interactionClearanceHalfLives?: import("@medassist/shared").InteractionClearanceHalfLives;
  proactiveSuggestions?: import("@medassist/shared").ProactiveSuggestionSettings;
  color?: string;
  waterGoalMl?: number;
  preferredFluidUnit?: import("@medassist/shared").FluidDisplayUnit;
  preferredHeightUnit?: import("@medassist/shared").HeightDisplayUnit;
  bowelDailyGoal?: number;
  bowelSoftConstipationDays?: number;
  bowelHardConstipationDays?: number;
  dashboardLayout?: import("@medassist/shared").DashboardWidgetPref[];
  complianceToleranceHours?: number;
  /** Weighted sessions/muscle/week for the Balance body map (D-TGT-1). Clamped [0.5, 7] server-side. */
  weeklyMuscleTargetSessions?: number;
  /** Working sets/muscle/week for the Balance body map (D-TA-5). Clamped [1, 60] server-side. */
  weeklySetTarget?: number;
  /** Count specialist muscles as Log recommendation gaps (D-IF-3). Absent on the patient = false. */
  includeSpecialistMuscles?: boolean;
  /** Per-patient training goal for sets/reps guidance (D-DG-2). */
  trainingGoal?: import("@medassist/shared").TrainingGoal;
}

export async function updatePreferences(patientId: string, prefs: PreferencesPatch): Promise<Patient> {
  // JSON.stringify drops `undefined` values, so spreading the patch sends only
  // the keys the caller actually set.
  return (
    await call<{ patient: Patient }>("/patients/preferences", "POST", { patientId, ...prefs })
  ).patient;
}

// ---- Chat capture ----
export interface ProposedEvent {
  eventType: string;
  payload: Record<string, unknown>;
  confirmation: string;
  /** Proactive suggestions (PROACTIVE_SUGGESTIONS_PLAN.md §10): the default "now"
   *  time and the med's scheduled/typical time the Now/Scheduled radio toggles
   *  between. Absent on AI chat proposals. */
  occurredAt?: string;
  scheduledAnchorAt?: string;
  /** Proactive suggestions Phase 2: which source produced this med. "learned" events
   *  (inferred from history, not in a saved regimen) get an "add to a regimen?" nudge. */
  source?: "scheduled" | "learned";
  /** ISO timestamp of the most recent non-missed dose. Present on proactive suggestion
   *  events so the UI can show "last dose: X ago". Absent on AI chat proposals. */
  lastDoseAt?: string;
}

/** Admin-only Bedrock interaction detail (cost transparency / debug inspector).
 *  Mirrors the `debug` field the API attaches for admin callers. The request
 *  body contains patient medical context, so it is only ever populated for
 *  admins and only shown in the admin debug surfaces. */
export interface BedrockDebug {
  mode: "capture" | "onboarding" | "analysis" | "deep" | "second_opinion";
  modelId: string;
  /** Set when an Anthropic API call failed and retried on Bedrock; the model that was configured. */
  fellBackFrom?: string;
  /** Absent for Deep / Second opinion turns. */
  request?: unknown;
  responseText: string;
  toolUses?: { name: string; input: Record<string, unknown> }[];
  inputTokens: number;
  outputTokens: number;
  durationMs?: number;
}

export interface InteractionProposalData {
  interactionWarnings: import("@medassist/shared").InteractionWarning[];
  hiddenInteractionCount: number;
  /** Exact drug set the warning was computed over — re-checked verbatim by the
   *  "View full interaction details" modal (proposed + recent meds, not the
   *  regimen). */
  checkedDrugNames: string[];
  /** The just-proposed meds (focus subset of checkedDrugNames). Warnings are
   *  limited to pairs touching one of these; passed to the modal so its re-check
   *  filters the same way. */
  focusDrugNames: string[];
  rxnavUnreachable: boolean;
  fallbackUsed: boolean;
}

export interface OverdoseProposalData {
  overdoseWarnings: import("@medassist/shared").OverdoseWarning[];
  hiddenOverdoseCount: number;
}

export interface FoodProposalData {
  foodGuidance: import("@medassist/shared").FoodGuidanceNoteItem[];
}

export interface SideEffectProposalData {
  sideEffectWarnings: import("@medassist/shared").SideEffectSymptomMatch[];
}

export type ChatResponse =
  | { type: "message"; text: string; sessionId: string; debug?: BedrockDebug }
  | ({ type: "proposal"; pendingId: string; confirmationText: string; events: ProposedEvent[]; sessionId: string; debug?: BedrockDebug; needsRatingItems?: NeedsRatingItem[] } & Partial<InteractionProposalData> & Partial<OverdoseProposalData> & Partial<FoodProposalData> & Partial<SideEffectProposalData>)
  | { type: "clarification"; prompt: string; dimension: string; options: MedVariantOption[]; context: MedClarificationContext; sessionId: string; debug?: BedrockDebug }
  | { type: "needs_rating"; items: NeedsRatingItem[]; sessionId: string; debug?: BedrockDebug };

// Re-export for ChatPane (avoids direct shared import in the component).
export type { MedVariantOption, MedClarificationContext };

export type ChatMode = "capture" | "onboarding";

export async function chat(
  patientId: string,
  message: string,
  sessionId?: string,
  mode: ChatMode = "capture",
  signal?: AbortSignal
): Promise<ChatResponse> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const res = await call<ChatResponse>("/chat", "POST", { patientId, message, sessionId, timezone, mode }, true, { signal });
  // Admin-only: the API attaches `debug` for admin callers — record it for the inspector.
  if (res.debug) {
    const d = res.debug;
    recordBedrockInteraction({
      mode: d.mode,
      modelId: d.modelId,
      request: d.request,
      responseText: d.responseText,
      toolUses: d.toolUses,
      inputTokens: d.inputTokens,
      outputTokens: d.outputTokens,
      durationMs: d.durationMs,
      fellBackFrom: d.fellBackFrom,
    });
  }
  return res;
}

/** Submit a user's clarification button selection. Returns a proposal response
 *  (the same shape as a normal chat proposal) ready for the confirm gate. */
export async function clarifySelect(
  patientId: string,
  option: MedVariantOption,
  context: MedClarificationContext,
  sessionId: string | undefined,
  signal?: AbortSignal
): Promise<Extract<ChatResponse, { type: "proposal" }>> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return call<Extract<ChatResponse, { type: "proposal" }>>(
    "/chat/clarify-select",
    "POST",
    { patientId, option, context, sessionId, timezone },
    true,
    { signal }
  );
}

// ---- Streaming chat (SSE over a Lambda Function URL, D-27/D-46) ----
export function streamingEnabled(): boolean {
  return !!STREAM_URL;
}

export interface StreamDoneInfo {
  /** seq of the assistant message this turn produced. */
  seq: number;
  depth: PremiumDepth;
  spend: { todayUsd: number; capUsd: number };
}

export interface StreamCallbacks {
  onSession?: (sessionId: string) => void;
  onDelta: (text: string) => void;
  onRedFlag?: (text: string) => void;
  onProposal: (p: { pendingId: string; confirmationText: string; events: ProposedEvent[]; needsRatingItems?: NeedsRatingItem[] } & Partial<InteractionProposalData> & Partial<OverdoseProposalData> & Partial<FoodProposalData> & Partial<SideEffectProposalData>) => void;
  onClarification?: (c: { prompt: string; dimension: string; options: MedVariantOption[]; context: MedClarificationContext; sessionId: string }) => void;
  onNeedsRating?: (n: { items: NeedsRatingItem[]; sessionId: string }) => void;
  /** `info` is present only for Deep / Second opinion turns (CHAT_DEPTH_MODES_PLAN Task 13). */
  onDone: (sessionId?: string, info?: StreamDoneInfo) => void;
  onError: (message: string) => void;
  /** Premium turns: first frame, naming the depth and the model that actually answers. */
  onDepth?: (d: { depth: PremiumDepth; modelId: string; fellBackFrom?: string }) => void;
  /** Premium turns: tool-round progress text ("" clears it). */
  onStatus?: (text: string) => void;
  /** Premium turns: verified citations for the final answer. */
  onCitations?: (items: Array<{ eventId: string; label: string }>) => void;
  /** Premium turns: the model suggests logging something new (never auto-logged). */
  onLogOffer?: (summary: string) => void;
  /** Admin-only: fires with the full Bedrock interaction detail for this turn. */
  onUsage?: (debug: BedrockDebug) => void;
}

/** Streams a capture turn. Falls back is the caller's job (use streamingEnabled). */
export async function streamChat(
  patientId: string,
  message: string,
  sessionId: string | undefined,
  cb: StreamCallbacks,
  signal?: AbortSignal,
  opts?: { depth?: ChatDepth; ofSeq?: number }
): Promise<void> {
  if (!STREAM_URL) throw new ApiError("Streaming is not configured", 0);
  const token = getToken();
  if (!token) throw new ApiError("Not signed in", 401);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  debugLog("info", "→ STREAM /chat");
  let res: Response;
  try {
    res = await fetch(STREAM_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        patientId,
        message,
        sessionId,
        timezone,
        ...(opts?.depth && opts.depth !== "quick" ? { depth: opts.depth } : {}),
        ...(opts?.ofSeq !== undefined ? { ofSeq: opts.ofSeq } : {}),
      }),
      signal,
    });
  } catch (networkErr) {
    if (isAbortError(networkErr)) {
      debugLog("info", "⊘ STREAM /chat — cancelled before response");
      throw networkErr;
    }
    const msg = networkErr instanceof Error ? networkErr.message : String(networkErr);
    debugLog("error", `✗ STREAM /chat — network error: ${msg}`, { url: STREAM_URL });
    throw new ApiError(`Network error streaming chat: ${msg}`, 0);
  }
  if (res.status === 401) {
    clearToken();
    throw new ApiError("Session expired — please sign in again", 401);
  }
  if (!res.ok || !res.body) {
    debugLog("error", `✗ STREAM /chat — ${res.status}`);
    throw new ApiError(`Stream failed (${res.status})`, res.status);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    // A cancel mid-stream rejects the pending read (and in some browsers only
    // surfaces on the next iteration) — treat both shapes as a clean stop so no
    // further frames reach the callbacks after the user hit stop.
    if (signal?.aborted) {
      debugLog("info", "⊘ STREAM /chat — cancelled mid-stream");
      throw new DOMException("Aborted", "AbortError");
    }
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch (readErr) {
      if (signal?.aborted || isAbortError(readErr)) {
        debugLog("info", "⊘ STREAM /chat — cancelled mid-stream");
        throw new DOMException("Aborted", "AbortError");
      }
      throw readErr;
    }
    const { done, value } = chunk;
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buf.indexOf("\n\n")) >= 0) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      let evt: Record<string, unknown>;
      try {
        evt = JSON.parse(dataLine.slice(5).trim());
      } catch {
        continue;
      }
      switch (evt.type) {
        case "session":
          cb.onSession?.(evt.sessionId as string);
          break;
        case "delta":
          cb.onDelta(evt.text as string);
          break;
        case "redflag":
          cb.onRedFlag?.(evt.text as string);
          break;
        case "proposal":
          cb.onProposal(
            evt as unknown as { pendingId: string; confirmationText: string; events: ProposedEvent[]; needsRatingItems?: NeedsRatingItem[] } & Partial<InteractionProposalData> & Partial<OverdoseProposalData> & Partial<FoodProposalData> & Partial<SideEffectProposalData>
          );
          break;
        case "clarification":
          cb.onClarification?.(evt as unknown as { prompt: string; dimension: string; options: MedVariantOption[]; context: MedClarificationContext; sessionId: string });
          break;
        case "needs_rating":
          cb.onNeedsRating?.(evt as unknown as { items: NeedsRatingItem[]; sessionId: string });
          break;
        case "debug": {
          const d = evt as unknown as BedrockDebug;
          recordBedrockInteraction({
            mode: d.mode,
            modelId: d.modelId,
            request: d.request,
            responseText: d.responseText,
            toolUses: d.toolUses,
            inputTokens: d.inputTokens,
            outputTokens: d.outputTokens,
            durationMs: d.durationMs,
            fellBackFrom: d.fellBackFrom,
          });
          cb.onUsage?.(d);
          break;
        }
        case "depth":
          cb.onDepth?.(evt as unknown as { depth: PremiumDepth; modelId: string; fellBackFrom?: string });
          break;
        case "status":
          cb.onStatus?.((evt.text as string) ?? "");
          break;
        case "citations":
          cb.onCitations?.((evt.items as Array<{ eventId: string; label: string }>) ?? []);
          break;
        case "log_offer":
          cb.onLogOffer?.(evt.summary as string);
          break;
        case "done":
          cb.onDone(
            evt.sessionId as string | undefined,
            evt.depth && evt.spend && typeof evt.seq === "number"
              ? { seq: evt.seq, depth: evt.depth as PremiumDepth, spend: evt.spend as { todayUsd: number; capUsd: number } }
              : undefined
          );
          break;
        case "error":
          cb.onError(
            evt.detail ? `${evt.message as string}: ${evt.detail as string}` : (evt.message as string)
          );
          break;
      }
    }
  }
  debugLog("info", "✓ STREAM /chat — closed");
}

// ---- Sessions & messages (conversation persistence, §7) ----
export async function getSessions(patientId: string, mode?: ChatMode): Promise<ChatSession[]> {
  const q = new URLSearchParams({ patientId });
  if (mode) q.set("mode", mode);
  return (await call<{ sessions: ChatSession[] }>(`/sessions?${q}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.events })).sessions;
}

export async function getMessages(patientId: string, sessionId: string): Promise<ChatMessage[]> {
  const q = new URLSearchParams({ patientId, sessionId });
  return (await call<{ messages: ChatMessage[] }>(`/messages?${q}`, "GET")).messages;
}

// ---- Conversation search (CONVERSATION_SEARCH_PLAN.md) ----
export async function searchConversations(
  patientId: string,
  query: string,
  scopes: SearchScope[]
): Promise<ConversationSearchResponse> {
  const q = new URLSearchParams({ patientId, q: query });
  if (scopes.length) q.set("scopes", scopes.join(","));
  return call<ConversationSearchResponse>(`/search?${q}`, "GET");
}

// ---- Per-mode model config (Admin Settings, §27, D-59) ----
export interface ModelSettings {
  models: ModelConfig;
  /** All-Bedrock config saved before a bulk switch to the Anthropic API, or null. */
  bedrockRestorePoint: ModelConfig | null;
  /** GET only: Anthropic API key present in SSM (true/false), or null/absent when unknown. */
  anthropicKeyConfigured?: boolean | null;
}

export async function getModelConfig(): Promise<ModelSettings> {
  return call<ModelSettings>("/settings/models", "GET");
}

export async function updateModelConfig(models: ModelConfig, bedrockRestorePoint?: ModelConfig): Promise<ModelSettings> {
  return call<ModelSettings>("/settings/models", "POST", bedrockRestorePoint ? { models, bedrockRestorePoint } : { models });
}

/** Admin: expire a global lookup cache so the next request re-fetches it.
 *  Returns the number of entries removed. */
export async function clearCache(kind: CacheKind): Promise<number> {
  return (await call<{ cleared: number }>("/admin/clear-cache", "POST", { kind })).cleared;
}

// ---- Regimens (§6.1.1, D-4) ----
export async function getRegimens(patientId: string): Promise<Regimen[]> {
  const q = new URLSearchParams({ patientId });
  return (await call<{ regimens: Regimen[] }>(`/regimens?${q}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list })).regimens;
}

export async function saveRegimen(regimen: Partial<Regimen>): Promise<Regimen> {
  return (await call<{ regimen: Regimen }>("/regimens", "POST", regimen)).regimen;
}

export async function deleteRegimen(patientId: string, regimenId: string): Promise<void> {
  await call("/regimens/delete", "POST", { patientId, regimenId });
}

/**
 * One-shot backfill of canonical RxCUIs onto existing regimen/individual-med
 * items for every patient the caller can access. Idempotent and cheap to re-run
 * (already-resolved items are skipped). Returns how many regimens were updated.
 */
export async function backfillRegimenRxcui(): Promise<{ patientsProcessed: number; regimensUpdated: number }> {
  return call<{ patientsProcessed: number; regimensUpdated: number }>("/regimens/backfill-rxcui", "POST", {});
}

export interface CleanupPreviewRow {
  lineId: string;
  regimenId: string;
  regimenName: string;
  currentName: string;
  proposedName: string;
  source: string;
}

export async function getCleanupPreview(patientId: string): Promise<CleanupPreviewRow[]> {
  const q = new URLSearchParams({ patientId });
  return (await call<{ rows: CleanupPreviewRow[] }>(`/medications/cleanup/preview?${q}`, "GET")).rows;
}

export async function applyCleanup(
  patientId: string,
  approved: { regimenId: string; lineId: string }[]
): Promise<{ renamedCount: number; regimensUpdated: number }> {
  return call<{ renamedCount: number; regimensUpdated: number }>(
    "/medications/cleanup/apply",
    "POST",
    { patientId, approved }
  );
}

export interface SavedEventRef {
  eventId: string;
  eventType: string;
  occurredAt: string;
}

export async function confirmEvents(
  patientId: string,
  pendingId: string,
  keepIndexes?: number[],
  takenAtMode?: "now" | "scheduled" | "custom",
  takenAtIso?: string,
  sessionId?: string
): Promise<{ saved: SavedEventRef[]; followUp?: { message: string } }> {
  const res = await call<{ saved: SavedEventRef[]; followUp?: { message: string } }>(
    "/events/confirm",
    "POST",
    {
      patientId,
      pendingId,
      keepIndexes,
      ...(takenAtMode ? { takenAtMode } : {}),
      ...(takenAtIso ? { takenAtIso } : {}),
      ...(sessionId ? { sessionId } : {}),
    }
  );
  return { saved: res.saved, ...(res.followUp ? { followUp: res.followUp } : {}) };
}

/** Complete a needs_rating proposal — user picked a 1-10 rating in InlineRatingCard.
 *  Returns the same proposal shape as a normal chat turn (INLINE_RATING_PLAN.md). */
export async function completeRating(
  patientId: string,
  sessionId: string,
  items: Array<{ eventType: RatingEventType; extractedFields: Record<string, unknown>; rating: number }>
): Promise<Extract<ChatResponse, { type: "proposal" }>> {
  return call<Extract<ChatResponse, { type: "proposal" }>>(
    "/events/complete-rating",
    "POST",
    { patientId, sessionId, items }
  );
}

// ---- Proactive suggestions (PROACTIVE_SUGGESTIONS_PLAN.md) ----

/** A proactive suggestion returned on chat-open. `prefilled` suggestions carry a
 *  stored pending batch (pendingId + events) the user can one-tap confirm.
 *  `symptom_follow_up` suggestions carry openSymptoms for the follow-up card stack. */
export type ProactiveSuggestion = {
  id: string;
  kind: "scheduled_medication" | "learned_medication" | "symptom_follow_up" | "pt_compliance" | "injury_follow_up" | "symptom_persistence_alert" | "illness_follow_up";
  shape: "prefilled" | "reminder";
  headline: string;
  pendingId: string;
  events: ProposedEvent[];
  /** Allergy / interaction / overdose warning markdown to show above the list. */
  warningPrefix?: string;
  /** Present for kind="symptom_follow_up" (SYMPTOM_FOLLOWUP_PLAN.md). */
  openSymptoms?: import("@medassist/shared").OpenSymptom[];
  /** Present for kind="illness_follow_up" (ILLNESS_EPISODE_TRACKING_PLAN.md). */
  openIllnesses?: import("@medassist/shared").OpenIllness[];
  /** Present for kind="symptom_persistence_alert" (SYMPTOM_PERSISTENCE_ALERT_PLAN.md). */
  persistenceAlert?: {
    key: string;
    name: string;
    daysPersisting: number;
    onsetAt: string;
    reasoning: string;
  };
} & Partial<InteractionProposalData> &
  Partial<OverdoseProposalData> &
  Partial<SideEffectProposalData>;

/** Ask the server what this patient usually takes around now (no Bedrock). Returns
 *  [] when disabled, cold-start, or nothing is due. */
export async function getSuggestions(patientId: string): Promise<ProactiveSuggestion[]> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const q = new URLSearchParams({ patientId, timezone });
  const res = await call<{ suggestions: ProactiveSuggestion[] }>(`/suggestions?${q.toString()}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list });
  return res.suggestions ?? [];
}

/** Build a pending medication_taken proposal from a saved regimen item — the
 *  dashboard as-needed quick-log (no Bedrock). Returns the same prefilled-suggestion
 *  shape as getSuggestions, so the caller confirms it via confirmEvents. `dose`
 *  overrides the saved single-ingredient dose (ignored for combination products). */
export async function quickLogMedication(
  patientId: string,
  regimenId: string,
  lineId: string,
  dose?: number,
  notes?: string
): Promise<ProactiveSuggestion> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return call<ProactiveSuggestion>("/medications/quick-log", "POST", {
    patientId,
    regimenId,
    lineId,
    ...(typeof dose === "number" ? { dose } : {}),
    ...(notes?.trim() ? { notes: notes.trim() } : {}),
    timezone,
  });
}

/** Build a pending medication_taken proposal for N saved regimen items at once —
 *  the dashboard multi-select quick-log (MEDICATION_BATCH_LOGGING_PLAN.md).
 *  Each item may include a `dose` (total dose in that item's unit); omit it to
 *  keep the saved default. Combination products reject a dose server-side
 *  (MEDICATION_BATCH_DOSE_ADJUST_PLAN.md). */
export async function quickLogMedicationBatch(
  patientId: string,
  items: { regimenId: string; lineId: string; dose?: number }[]
): Promise<ProactiveSuggestion> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return call<ProactiveSuggestion>("/medications/quick-log-batch", "POST", { patientId, items, timezone });
}

/** Re-run the five safety gates on an in-progress quick-log batch after dose edits
 *  (MEDICATION_BATCH_DOSE_ADJUST_PLAN.md). Returns a fresh pendingId; events stay
 *  in the same order. */
export async function reviseSuggestionDoses(
  patientId: string,
  pendingId: string,
  edits: { index: number; dose: number }[]
): Promise<ProactiveSuggestion> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return call<ProactiveSuggestion>("/medications/quick-log/revise-batch", "POST", {
    patientId,
    pendingId,
    edits,
    timezone,
  });
}

/** Fold one more saved medication into an in-progress suggestion's pending batch
 *  (MEDICATION_BATCH_LOGGING_PLAN.md "Add another medication"). `keepIndexes` are
 *  indices into the ORIGINAL pending's events — pass through whichever rows the
 *  user hasn't removed so they stay excluded from the rebuilt batch. */
export async function addMedicationToSuggestion(
  patientId: string,
  pendingId: string,
  regimenId: string,
  lineId: string,
  keepIndexes?: number[]
): Promise<ProactiveSuggestion> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return call<ProactiveSuggestion>("/medications/quick-log/add-to-batch", "POST", {
    patientId,
    pendingId,
    regimenId,
    lineId,
    ...(keepIndexes ? { keepIndexes } : {}),
    timezone,
  });
}

/** Per-active-medication "would this overdose if taken right now" severity, keyed
 *  by `${regimenId}:${lineId}` — powers the red highlight in the quick-log picker
 *  (MEDICATION_OVERDOSE_PLAN.md). Cached briefly (CACHE_TTL.list); any dose logged
 *  clears the whole read cache, so this stays correct after a write
 *  (API_COLD_START_AND_REQUEST_FANOUT_PLAN.md Task 3.3). */
export async function getMedicationOverdoseCheck(
  patientId: string
): Promise<Map<string, import("@medassist/shared").OverdoseSeverity | null>> {
  const q = new URLSearchParams({ patientId });
  const res = await call<{ results: { key: string; severity: import("@medassist/shared").OverdoseSeverity | null }[] }>(
    `/medications/overdose-check?${q}`,
    "GET",
    undefined,
    true,
    { cacheTtlMs: CACHE_TTL.list }
  );
  return new Map(res.results.map((r) => [r.key, r.severity]));
}

/** Missed scheduled doses from the past 7 days (MISSED_DOSE_BACKFILL_PLAN.md), zero
 *  Bedrock. Used by the dashboard "Missed doses" widget. */
export async function getBackfillSlots(patientId: string): Promise<BackfillSlotsResponse> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const q = new URLSearchParams({ patientId, timezone });
  return call<BackfillSlotsResponse>(`/medications/backfill?${q.toString()}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list });
}

/** Direct-save staged backfill decisions ("Took it" / "Didn't take it") — no
 *  pending/confirm, no safety gates (D-BF-8). */
export async function logBackfillEntries(
  patientId: string,
  entries: BackfillLogEntry[]
): Promise<{ saved: { eventId: string; eventType: string; occurredAt: string; slotId: string }[]; skipped: { slotId: string; reason: string }[] }> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return call("/medications/backfill/log", "POST", { patientId, timezone, entries });
}

/** Per-type forgot-to-log sweep for the enabled catch-up widgets
 *  (CATCHUP_WIDGETS_PLAN.md), zero Bedrock. Used by the bowel/sleep/pt/gym/vitals
 *  dashboard widgets via `catchUpFetch.ts`'s dedupe. */
export async function getCatchUp(patientId: string): Promise<CatchUpResponse> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const q = new URLSearchParams({ patientId, timezone });
  return call<CatchUpResponse>(`/catch-up?${q.toString()}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list });
}

/** Direct-save staged catch-up decisions (bowel/sleep/pt) from the catch-up
 *  modals — no pending/confirm, no Bedrock (D-CU-11). */
export async function logCatchUpEntries(
  patientId: string,
  entries: CatchUpLogEntry[]
): Promise<CatchUpLogResult> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return call<CatchUpLogResult>("/catch-up/log", "POST", { patientId, timezone, entries });
}

/** Admin-only: fetch the proactive-suggestion engine diagnostics for "why is this
 *  med (not) suggested?". Returns the same suggestions plus a `debug` snapshot of the
 *  timing math and per-candidate verdicts. `debug` is only populated for admin callers. */
export async function getSuggestionsDebug(
  patientId: string
): Promise<{
  suggestions: ProactiveSuggestion[];
  debug?: import("@medassist/shared").ProactiveSuggestionDebug;
}> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const q = new URLSearchParams({ patientId, timezone, debug: "1" });
  return call(`/suggestions?${q.toString()}`, "GET");
}

// ---- Symptom follow-up (SYMPTOM_FOLLOWUP_PLAN.md §4.2 / §4.4) ----

/** Record a one-tap severity update or resolution for an open symptom/headache. */
export async function recordSymptomFollowUp(body: {
  patientId: string;
  key: string;
  eventType: "symptom" | "headache";
  severity: number;
  resolved?: boolean;
  occurredAt?: string;
  notes?: string;
  /** D-PAIN-4: a pain-site id. When set, the check-in is written against that
   *  site and its bodyMap is copied forward server-side (PAIN_TRACKING_PLAN Task 5.2). */
  siteId?: string;
}): Promise<{ eventId: string; occurredAt: string }> {
  return call("/symptom-follow-up", "POST", body);
}

/** Snooze a follow-up card for the patient's configured snooze window (D-SF-9). */
export async function snoozeSymptomFollowUp(body: {
  patientId: string;
  key: string;
}): Promise<{ snoozedUntil: string }> {
  return call("/symptom-follow-up/snooze", "POST", body);
}

/** Set the per-symptom reminder interval (SYMPTOM_REMINDER_INTERVAL_PLAN.md, D-SF-I2). */
export async function setSymptomReminderInterval(body: {
  patientId: string;
  key: string;
  hours: number;
}): Promise<{ hours: number }> {
  return call("/symptom-follow-up/reminder-interval", "POST", body);
}

// ---- Acute-illness follow-up (ILLNESS_EPISODE_TRACKING_PLAN.md, D-IE-3/9/12) ----

/** Record a one-tap acute-illness check-in ("still sick" / "recovered" / "worse")
 *  for an open episodic illness. Mirrors recordSymptomFollowUp. */
export async function recordIllnessFollowUp(body: {
  patientId: string;
  key: string;
  action: "same" | "recovered" | "worse";
  recoveredOn?: string;
  severity?: "mild" | "moderate" | "severe";
  timezone?: string;
}): Promise<{ event: MedicalEvent }> {
  return call("/illness-follow-up", "POST", body);
}

/** Snooze an illness follow-up card for the patient's configured window (D-IE-12). */
export async function snoozeIllnessFollowUp(body: {
  patientId: string;
  key: string;
}): Promise<{ snoozedUntil: string }> {
  return call("/illness-follow-up/snooze", "POST", body);
}

/** Set the per-illness reminder interval in days (D-IE-9). */
export async function setIllnessReminderInterval(body: {
  patientId: string;
  key: string;
  days: number;
}): Promise<{ intervals: Record<string, number> }> {
  return call("/illness-follow-up/reminder-interval", "POST", body);
}

// ---- Symptom persistence alerts (SYMPTOM_PERSISTENCE_ALERT_PLAN.md) ----

/** Dismiss a persistent-symptom "see a doctor" alert. It can resurface on the
 *  next scheduled re-evaluation if the model still judges it a concern (D-SPA-9). */
export async function dismissSymptomPersistenceAlert(patientId: string, key: string): Promise<void> {
  await call("/symptom-persistence-alerts/dismiss", "POST", { patientId, key });
}

export async function undoEvent(
  patientId: string,
  occurredAt: string,
  eventType: string,
  eventId: string
): Promise<void> {
  await call("/events/undo", "POST", { patientId, occurredAt, eventType, eventId });
}

/** Edit a saved drink from Log Water. `occurredAt` is the event's current key. */
export async function patchHydrationEvent(
  patientId: string,
  eventId: string,
  occurredAt: string,
  edit: { volume: number; unit: "oz" | "mL"; fluidType?: string; consumedAt: string },
): Promise<MedicalEvent> {
  return (
    await call<{ ok: true; event: MedicalEvent }>("/events/hydration", "PATCH", {
      patientId,
      eventId,
      occurredAt,
      ...edit,
    })
  ).event;
}

export async function createNote(
  patientId: string,
  body: string,
  title?: string,
  recordedAt?: string
): Promise<{ eventId: string; recordedAt: string }> {
  return call("/notes", "POST", { patientId, body, ...(title ? { title } : {}), ...(recordedAt ? { recordedAt } : {}) });
}

/**
 * Lists a patient's events, newest-first.
 * - `type` restricts to one event type (served from GSI1).
 * - `limit` caps how many of the newest events are returned. Only use it where the
 *   caller truly needs just the most recent records — e.g. a snapshot — never where
 *   it must see the full history (PERFORMANCE_PLAN.md #2).
 */
export async function listEvents(patientId: string, type?: string, limit?: number): Promise<MedicalEvent[]> {
  const q = new URLSearchParams({ patientId });
  if (type) q.set("type", type);
  if (limit) q.set("limit", String(limit));
  return (await call<{ events: MedicalEvent[] }>(`/events?${q.toString()}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.events })).events;
}

/** Event types the follow-up reminder banner inspects (see web/src/lib/notifications.ts).
 *  Fetching only these — instead of the whole timeline — keeps the Shell's notification
 *  load cheap and bounded by how many of *these* low-volume events exist. */
const NOTIFICATION_EVENT_TYPES = ["stress_event", "hospitalization", "visit", "vaccination"] as const;

/** Fetches just the events the reminder banner needs, in one request
 *  (API_COLD_START_AND_REQUEST_FANOUT_PLAN.md Phase 3 — was 4 parallel requests). */
export async function listNotificationEvents(patientId: string): Promise<MedicalEvent[]> {
  const q = new URLSearchParams({ patientId, types: NOTIFICATION_EVENT_TYPES.join(",") });
  return (await call<{ events: MedicalEvent[] }>(
    `/events?${q.toString()}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.events },
  )).events;
}

/** Returns the patient's pt_program events (PHYSICAL_THERAPY_PLAN.md Phase 3). */
export async function listPtPrograms(patientId: string): Promise<MedicalEvent[]> {
  const q = new URLSearchParams({ patientId });
  return (await call<{ programs: MedicalEvent[] }>(`/pt/programs?${q.toString()}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list })).programs;
}

// ---- PT compliance report (PHYSICAL_THERAPY_PLAN.md Phase 6) ----

export interface PtExerciseCompliance {
  exerciseId: string;
  exerciseName: string;
  frequencyRecommended: string;
  expectedTimeToResults: string;
  prescribedInWindow: number;
  actualInWindow: number;
  adherencePct: number;
  currentStreak: number;
  longestStreak: number;
  lastLoggedAt?: string;
}

export interface PtProgramCompliance {
  programEventId: string;
  injuryEventId?: string;
  bodyRegion?: string;
  suspectedDiagnosis?: string;
  programStartedAt: string;
  source: "ai_catalog" | "scanned";
  sheetTitle?: string;
  cadence?: string;
  exercises: PtExerciseCompliance[];
  currentStreak: number;
  longestStreak: number;
  weeklyPct: number;
  weeklyGoalMet: boolean;
  injuryStatus?: string;
  injuryFollowUps?: { date: string; note: string; status: string }[];
}

export interface PtComplianceReport {
  days: number;
  programs: PtProgramCompliance[];
  /** Distinct exercises logged per day for the last 7 calendar days, across all programs. */
  last7Days: { date: string; count: number }[];
}

export async function getPtComplianceReport(
  patientId: string,
  days: number,
): Promise<PtComplianceReport> {
  const q = new URLSearchParams({ patientId, days: String(days) });
  return call<PtComplianceReport>(`/pt/compliance?${q.toString()}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list });
}

// ---- Trainer: gym logging (PERSONAL_TRAINER_PLAN.md Phase 2) ----

/** GET /gym/exercises — the Log tab's grid buttons, server-sorted recent first.
 *  Defaults to `household` scope (D-HH-2/3): every household member's equipment, with
 *  THIS patient's own prefill/recency merged in. Pass `"mine"` for own equipment only. */
export async function listGymExercises(
  patientId: string,
  includeArchived?: boolean,
  scope?: "mine" | "household"
): Promise<GymExerciseView[]> {
  const q = new URLSearchParams({ patientId });
  if (includeArchived) q.set("includeArchived", "1");
  if (scope) q.set("scope", scope);
  return (await call<{ exercises: GymExerciseView[] }>(`/gym/exercises?${q.toString()}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list })).exercises;
}

/** POST /gym/exercises — creates a UserGymExercise from a catalog match or as a custom entry. */
export async function createGymExercise(body: {
  patientId: string;
  catalogId?: string;
  name?: string;
  equipmentType?: UserGymExercise["equipmentType"];
  primaryMuscles?: MuscleGroup[];
  secondaryMuscles?: MuscleGroup[];
  primaryMuscleDetail?: string[];
  secondaryMuscleDetail?: string[];
  primaryMuscleIds?: AnatomicalMuscle[];
  secondaryMuscleIds?: AnatomicalMuscle[];
  metrics?: GymMetric[];
  gymLabel?: string;
  brand?: string;
  infoUrl?: string;
  photoKey?: string;
  thumbKey?: string;
  extraPhotoKeys?: string[];
  /** True when the equipment works one side of the body at a time (D-LAT-1). */
  unilateral?: boolean;
  /** A visible community entry id; the server copies the taxonomy from the record (client tags ignored). */
  communityId?: string;
}): Promise<UserGymExercise> {
  return (await call<{ exercise: UserGymExercise }>("/gym/exercises", "POST", body)).exercise;
}

/** POST /gym/equip/merge — fold a freshly-analyzed equipment draft into an existing
 *  household definition instead of creating a duplicate (D-8/9). Fill-if-absent /
 *  union only; the server applies only what the target is missing. */
export async function mergeGymEquipment(body: {
  patientId: string;
  /** Partition holding the target definition (a household member's, or the caller's own). */
  ownerPatientId: string;
  userExerciseId: string;
  /** Original photo keys to append; each must live under a household member's `gymequip/<owner>/` prefix. */
  addPhotoKeys?: string[];
  /** Applied only when the target has no thumbKey yet. */
  addThumbKey?: string;
  /** Fill-if-absent / union-only field set; the server never overwrites a set field (D-8). */
  fill?: {
    catalogId?: string;
    brand?: string;
    infoUrl?: string;
    primaryMuscles?: MuscleGroup[];
    secondaryMuscles?: MuscleGroup[];
    primaryMuscleDetail?: string[];
    secondaryMuscleDetail?: string[];
    primaryMuscleIds?: AnatomicalMuscle[];
    secondaryMuscleIds?: AnatomicalMuscle[];
    metrics?: GymMetric[];
  };
}): Promise<{ exercise: UserGymExercise; photosAdded: number; photosSkipped: number }> {
  return call<{ exercise: UserGymExercise; photosAdded: number; photosSkipped: number }>(
    "/gym/equip/merge",
    "POST",
    body,
  );
}

export interface GymLogMetrics {
  sets?: number;
  reps?: number;
  weight?: number;
  weightUnit?: "lbs" | "kg";
  durationMin?: number;
  distanceMi?: number;
  distanceUnit?: import("@medassist/shared").DistanceUnit;
  laps?: number;
  resistanceLevel?: number;
  incline?: number;
}

/** POST /gym/log — direct-save a gym_exercise event (D-GYM-2, no Bedrock/pending proposal). */
export async function logGymExercise(body: {
  patientId: string;
  userExerciseId: string;
  performedAt?: string;
  metrics?: GymLogMetrics;
  notes?: string;
  /** Partition holding the UserGymExercise definition; omit when the patient owns it (D-HH-4). */
  ownerPatientId?: string;
  /** Which side worked, for `unilateral` equipment (D-LAT-1/2). "both"/omitted are equivalent. */
  side?: import("@medassist/shared").ExerciseSide;
}): Promise<MedicalEvent> {
  return (await call<{ event: MedicalEvent }>("/gym/log", "POST", body)).event;
}

/** PATCH /gym/exercises — edit name/muscles/metrics/gymLabel/archived on a UserGymExercise. */
export async function updateGymExercise(body: {
  patientId: string;
  userExerciseId: string;
  name?: string;
  primaryMuscles?: MuscleGroup[];
  secondaryMuscles?: MuscleGroup[];
  primaryMuscleDetail?: string[];
  secondaryMuscleDetail?: string[];
  primaryMuscleIds?: AnatomicalMuscle[];
  secondaryMuscleIds?: AnatomicalMuscle[];
  metrics?: GymMetric[];
  gymLabel?: string;
  brand?: string;
  infoUrl?: string;
  archived?: boolean;
  /** True when the equipment works one side of the body at a time (D-LAT-1). */
  unilateral?: boolean;
  /** Reorders stored photos so this key becomes the default shown everywhere (D-4). */
  primaryPhotoKey?: string;
  /** Partition holding the definition; any household member may edit (D-HH-6). */
  ownerPatientId?: string;
}): Promise<UserGymExercise> {
  return (await call<{ exercise: UserGymExercise }>("/gym/exercises", "PATCH", body)).exercise;
}

/** DELETE /gym/exercises — hard-deletes a UserGymExercise + its S3 photos. Unlike
 *  archiving (which hides it but keeps it), this permanently removes the entry —
 *  use for a misidentified item you want to remove and try again. */
export async function deleteGymExercise(
  patientId: string,
  userExerciseId: string,
  ownerPatientId?: string
): Promise<void> {
  const q = new URLSearchParams({ patientId, userExerciseId });
  if (ownerPatientId) q.set("ownerPatientId", ownerPatientId);
  await call<{ ok: boolean }>(`/gym/exercises?${q.toString()}`, "DELETE");
}

/** PATCH /gym/usage — hide/unhide another household member's equipment on MY grid
 *  (D-HH-6). The non-owner's alternative to delete, which is owner-only. */
export async function setGymExerciseHidden(
  patientId: string,
  ownerPatientId: string,
  userExerciseId: string,
  hidden: boolean
): Promise<void> {
  await call<{ ok: boolean }>("/gym/usage", "PATCH", {
    patientId,
    ownerPatientId,
    userExerciseId,
    hidden,
  });
}

// ---- Trainer: gym equipment photo ID (PERSONAL_TRAINER_PLAN.md Phase 3, D-GYM-3) ----

export interface GymEquipIdentifyAlternative {
  catalogId: string;
  name: string;
}

/** The editable confirm-card payload returned by gym equipment photo identification. */
export interface GymEquipIdentifyResult {
  catalogId: string | null;
  name: string;
  equipmentType: UserGymExercise["equipmentType"];
  primaryMuscles: MuscleGroup[];
  secondaryMuscles: MuscleGroup[];
  primaryMuscleDetail: string[];
  secondaryMuscleDetail: string[];
  primaryMuscleIds: AnatomicalMuscle[];
  secondaryMuscleIds: AnatomicalMuscle[];
  metrics: GymMetric[];
  confidence: "high" | "medium" | "low";
  alternatives: GymEquipIdentifyAlternative[];
  brand: string | null;
  infoUrl: string | null;
  /** True when the machine works one side of the body at a time (D-LAT-1). */
  unilateral: boolean;
}

/** Presigned PUT URLs for a gym equipment photo: the original + a client-made thumbnail. */
export async function getGymEquipUploadUrl(
  patientId: string,
  fileName: string,
  mimeType: string
): Promise<{ uploadUrl: string; thumbUploadUrl: string; s3Key: string; thumbS3Key: string; userExerciseId: string }> {
  const q = new URLSearchParams({ patientId, fileName, mimeType });
  return call(`/gym/equip/upload-url?${q}`, "GET");
}

/** Run Bedrock vision over 1–3 uploaded gym equipment photos, grounded against the gym catalog. */
export async function identifyGymEquip(patientId: string, s3Keys: string[]): Promise<GymEquipIdentifyResult> {
  return call("/gym/equip/identify", "POST", { patientId, s3Keys });
}

/** Bedrock names a free-text-described exercise, grounded against the catalog. */
export async function describeGymExercise(
  patientId: string,
  description: string
): Promise<DescribeExerciseResult> {
  return call("/gym/exercise/describe", "POST", { patientId, description });
}

/** GET /gym/catalog/community - the visible community exercise index. A plain fetch: the server
 *  sends `Cache-Control: private, max-age=300` plus an ETag, so the browser HTTP cache handles
 *  revalidation itself (no If-None-Match here: CORS does not allow that header or expose ETag). */
export async function getCommunityIndex(): Promise<CommunityIndexEntry[]> {
  return (await call<{ entries: CommunityIndexEntry[] }>("/gym/catalog/community", "GET")).entries;
}

/** POST /gym/exercise/research - cached catalog/community lookup, else capped one-time AI
 *  research of the gated exercise name (COMMUNITY_EXERCISE_CATALOG_PLAN.md). */
export async function researchGymExercise(patientId: string, name: string): Promise<ResearchResult> {
  return call("/gym/exercise/research", "POST", { patientId, name });
}

/** Re-runs Bedrock classification against an ALREADY-SAVED equipment item (by
 *  userExerciseId) — reuses its stored photo(s) when present, or falls back to
 *  a text-only classification from its name/brand/catalogId. Lets muscle tags
 *  be refreshed after the taxonomy grows, without deleting and re-adding the
 *  item. Returns a suggestion only; nothing is saved until the caller applies
 *  it via updateGymExercise. */
export async function reclassifyGymExercise(
  patientId: string,
  userExerciseId: string
): Promise<GymEquipIdentifyResult> {
  return call("/gym/exercise/reclassify", "POST", { patientId, userExerciseId });
}

/** GET /gym/exercise/howto — step-by-step how-to for an exercise, identified by
 *  catalog id, an existing grid item's userExerciseId, or a free-text name.
 *  `source` distinguishes a curated entry, a cache hit, and a fresh generation
 *  (TRAINER_HOUSEHOLD_AND_LATERALITY_PLAN.md D-HOW-1). */
export async function getGymHowTo(
  patientId: string,
  ref: { catalogId?: string; userExerciseId?: string; name?: string; domain?: "trainer" | "physical_therapy" }
): Promise<{
  howTo: GymHowTo;
  source: "curated" | "cache" | "generated";
  /** Stable public registry identity; no database keys or patient content. */
  exerciseId: string;
  /** Repository-deployed public SVG metadata, when reviewed visuals exist. */
  visuals?: DeployedExerciseVisualContent;
}> {
  const q = new URLSearchParams({ patientId });
  if (ref.catalogId) q.set("catalogId", ref.catalogId);
  if (ref.userExerciseId) q.set("userExerciseId", ref.userExerciseId);
  if (ref.name) q.set("name", ref.name);
  if (ref.domain) q.set("domain", ref.domain);
  return call(`/gym/exercise/howto?${q.toString()}`, "GET");
}

/** GET /gym/exercise/dosing - goal-based sets/reps guidance for an exercise, identified like
 *  getGymHowTo. 409 = not-applicable equipment, 422 = redacted identity (both ApiError).
 *  The guide holds all six goals; the client picks the variant (D-DG-5). */
export async function getGymDosing(
  patientId: string,
  ref: { catalogId?: string; userExerciseId?: string; name?: string }
): Promise<{ guide: import("@medassist/shared").ExerciseDosingGuide; source: "cache" | "generated" | "baseline" }> {
  const q = new URLSearchParams({ patientId });
  if (ref.catalogId) q.set("catalogId", ref.catalogId);
  if (ref.userExerciseId) q.set("userExerciseId", ref.userExerciseId);
  if (ref.name) q.set("name", ref.name);
  return call(`/gym/exercise/dosing?${q.toString()}`, "GET");
}

// ---- Admin: global exercise/content registry ----

export async function listGlobalExerciseRegistry(): Promise<GlobalExerciseRecord[]> {
  return (await call<{ items: GlobalExerciseRecord[] }>("/admin/exercises", "GET")).items;
}

export async function getGlobalExerciseContentBatch(
  workflow: ExerciseContentWorkflow,
  limit: number,
): Promise<ExerciseContentBatch> {
  const query = new URLSearchParams({ workflow, limit: String(limit) });
  return call<ExerciseContentBatch>(`/admin/exercises/batch?${query}`, "GET");
}

export async function patchGlobalExerciseRegistry(input: {
  exerciseId: string;
  canonicalName?: string;
  aliases?: string[];
  reviewStatus?: ExerciseReviewStatus;
  mergedIntoExerciseId?: string | null;
  /** Community catalog edits: the published toggle and/or taxonomy fields (server re-validates). */
  community?: AdminCommunityPatch;
}): Promise<GlobalExerciseRecord> {
  return (await call<{ item: GlobalExerciseRecord }>("/admin/exercises", "PATCH", input)).item;
}

/** Fields the admin may change on a community entry (api applyAdminCommunityPatch). */
export interface AdminCommunityPatch {
  published?: boolean;
  equipmentType?: CommunityTaxonomy["equipmentType"];
  primaryMuscles?: CommunityTaxonomy["primaryMuscles"];
  secondaryMuscles?: CommunityTaxonomy["secondaryMuscles"];
  primaryMuscleIds?: CommunityTaxonomy["primaryMuscleIds"];
  secondaryMuscleIds?: CommunityTaxonomy["secondaryMuscleIds"];
  metrics?: CommunityTaxonomy["metrics"];
  unilateral?: boolean;
  description?: string;
  confidence?: CommunityTaxonomy["confidence"];
}

/** Admin: the community catalog kill-switch and daily caps. */
export async function getCommunityConfig(): Promise<CommunityConfig> {
  return (await call<{ config: CommunityConfig }>("/admin/exercises/community-config", "GET")).config;
}

/** Admin: partial update of the community config. Sent as PATCH on purpose: the HttpApi CORS
 *  allowMethods has no PUT, so a browser PUT would fail its preflight (the server accepts both). */
export async function putCommunityConfig(input: Partial<CommunityConfig>): Promise<CommunityConfig> {
  return (await call<{ config: CommunityConfig }>("/admin/exercises/community-config", "PATCH", input)).config;
}

export async function bulkReviewGlobalExerciseRegistry(
  decisions: ExerciseReviewDecision[],
  dryRun: boolean,
): Promise<ExerciseReviewBulkResult> {
  return call<ExerciseReviewBulkResult>("/admin/exercises/review/bulk", "POST", {
    decisions,
    dryRun,
    ...(dryRun ? {} : { confirm: "APPLY_EXERCISE_REVIEW_DECISIONS" }),
  });
}

export interface ExerciseRegistryBackfillResult {
  scanned: number;
  admitted: number;
  redacted: number;
}

export async function backfillGlobalExerciseRegistry(): Promise<ExerciseRegistryBackfillResult> {
  return call<ExerciseRegistryBackfillResult>("/admin/exercises/backfill", "POST");
}

export interface ExerciseRegistryStaleResult {
  examined: number;
  marked: number;
  skippedPermanent: number;
}

export async function markGeneratedExerciseContentStale(input: {
  contentKinds?: Array<"instructions" | "animatedSvg" | "stepVisuals">;
  schemaVersion?: number;
  promptVersion?: number;
}): Promise<ExerciseRegistryStaleResult> {
  return call<ExerciseRegistryStaleResult>("/admin/exercises/mark-stale", "POST", {
    ...input,
    confirm: "MARK_GENERATED_CONTENT_STALE",
  });
}

/** Presigned GET URL to display a retained gym equipment photo / thumbnail. */
export async function getGymEquipViewUrl(patientId: string, s3Key: string): Promise<string> {
  const qs = new URLSearchParams({ patientId, s3Key });
  return (await call<{ url: string }>(`/gym/equip/view-url?${qs}`, "GET")).url;
}

// ---- Trainer: Balance tab AI recommendations (PERSONAL_TRAINER_PLAN.md Phase 4, D-GYM-4) ----

/** GET /gym/recommendations — the cached recommendation, or null if none has been generated yet. */
export async function getGymRecommendations(patientId: string): Promise<GymRecommendationResult | null> {
  const qs = new URLSearchParams({ patientId });
  return (await call<{ recommendation: GymRecommendationResult | null }>(`/gym/recommendations?${qs}`, "GET")).recommendation;
}

/** POST /gym/recommendations — generates (and caches) a fresh AI balance recommendation. */
export async function generateGymRecommendations(patientId: string): Promise<GymRecommendationResult> {
  return (await call<{ recommendation: GymRecommendationResult }>("/gym/recommendations", "POST", { patientId })).recommendation;
}

/** GET /gym/workout-today — today's cached AI workout, or null. */
export async function getWorkoutToday(patientId: string): Promise<WorkoutTodayResult | null> {
  const qs = new URLSearchParams({ patientId });
  return (await call<{ workout: WorkoutTodayResult | null }>(`/gym/workout-today?${qs}`, "GET")).workout;
}

/** POST /gym/workout-today — generates (and caches) a fresh AI workout for today. */
export async function generateWorkoutToday(patientId: string, options: WorkoutTodayOptions): Promise<WorkoutTodayResult> {
  return (await call<{ workout: WorkoutTodayResult }>("/gym/workout-today", "POST", { patientId, options })).workout;
}

// ---- Trainer: AI workout program generator (PERSONAL_TRAINER_PLAN.md Phase 6, D-GYM-8) ----

/** GET /gym/programs — gym_program events for a patient. */
export async function listGymPrograms(patientId: string): Promise<MedicalEvent[]> {
  const q = new URLSearchParams({ patientId });
  return (await call<{ programs: MedicalEvent[] }>(`/gym/programs?${q.toString()}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list })).programs;
}

/** Result of POST /gym/program — either a refusal (see a doctor first) or the saved program. */
export type GymProgramResult =
  | { refusal: string; eventId?: undefined; occurredAt?: undefined; program?: undefined }
  | { refusal?: undefined; eventId: string; occurredAt: string; program: Record<string, unknown> };

/** POST /gym/program — generates (and saves) an AI starter workout program from a short intake. */
export async function generateGymProgram(body: {
  patientId: string;
  activityLevel: GymProgramIntake["activityLevel"];
  goals: string[];
  daysPerWeek: number;
  sessionLengthMin?: number;
  limitations?: string;
  activeInjuryEventIds?: string[];
}): Promise<GymProgramResult> {
  return call<GymProgramResult>("/gym/program", "POST", body);
}

/** POST /gym/program/archive — toggles the archived flag on a gym_program event. */
export async function setGymProgramArchived(payload: {
  patientId: string;
  eventId: string;
  occurredAt: string;
  archived: boolean;
}): Promise<void> {
  await call("/gym/program/archive", "POST", payload);
}

/**
 * Lazy-fetches the full notes text for an event whose payload has `notesRef` set
 * (MEDICAL_HISTORY_EVENTS_PLAN.md §7 — large-notes overflow). Only called when
 * the user expands an entry that has overflowed to S3.
 */
export async function getEventNotes(patientId: string, s3Key: string): Promise<string> {
  const q = new URLSearchParams({ patientId, s3Key });
  return (await call<{ notes: string }>(`/events/notes?${q.toString()}`, "GET")).notes;
}

// ---- Trash view (§19.12, D-34) ----

export async function listDeletedEvents(patientId: string): Promise<MedicalEvent[]> {
  const q = new URLSearchParams({ patientId });
  return (await call<{ events: MedicalEvent[] }>(`/events/deleted?${q}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list })).events;
}

/** Returns just the count of soft-deleted events — for the Trash badge. Avoids the
 *  full payload fetch listDeletedEvents does (PERFORMANCE_PLAN.md). */
export async function countDeletedEvents(patientId: string): Promise<number> {
  const q = new URLSearchParams({ patientId, count: "1" });
  return (await call<{ count: number }>(`/events/deleted?${q}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list })).count;
}

export async function restoreEvent(
  patientId: string,
  occurredAt: string,
  eventType: string,
  eventId: string
): Promise<void> {
  await call("/events/restore", "POST", { patientId, occurredAt, eventType, eventId });
}

export async function hardDeleteEvent(
  patientId: string,
  occurredAt: string,
  eventType: string,
  eventId: string,
  photoKeys?: string[]
): Promise<void> {
  await call("/events/hard-delete", "POST", { patientId, occurredAt, eventType, eventId, photoKeys });
}

// ---- Direct event save (wizard flows — family_history, allergy) ----

export interface BatchSaveRef {
  eventId: string;
  eventType: string;
  occurredAt: string;
}

export async function saveBatchEvents(
  patientId: string,
  events: { eventType: string; payload: Record<string, unknown> }[]
): Promise<BatchSaveRef[]> {
  return (await call<{ saved: BatchSaveRef[] }>("/events/save-batch", "POST", { patientId, events })).saved;
}

/** Rates (or re-rates) a saved treatment's effect (TREATMENT_LOGGING_PLAN.md, D-TX-3).
 *  `occurredAt` is required to locate the event's composite key — pass it from the
 *  `MedicalEvent` object already in hand (e.g. from a `listEvents` result). */
export async function patchTreatmentEffect(
  patientId: string,
  eventId: string,
  occurredAt: string,
  effect: TreatmentEffect,
  notes?: string,
): Promise<MedicalEvent> {
  return (
    await call<{ ok: true; event: MedicalEvent }>("/events/treatment", "PATCH", {
      patientId, eventId, occurredAt, effect, ...(notes ? { notes } : {}),
    })
  ).event;
}

// ---- Cost guardrails (§8.4.3) ----

export interface CostCap {
  dailyTokenLimit?: number;
  monthlyTokenLimit?: number;
  action: "warn" | "block";
  /** Per-patient daily dollar cap per premium chat mode. Omitted = default; 0 = mode off. */
  depthDailyUsd?: { deep?: number; second_opinion?: number };
  /** Household monthly dollar ceiling across Deep and Second opinion. Omitted = default; 0 = off. */
  depthMonthlyHouseholdUsd?: number;
}

export interface TokenUsageDay {
  dateKey: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface CostUsage {
  today: TokenUsageDay;
  monthTotal: number;
  monthDays: TokenUsageDay[];
  cap: CostCap;
}

export async function getCostCap(): Promise<CostCap> {
  return (await call<{ cap: CostCap }>("/admin/cost-cap", "GET")).cap;
}

export async function updateCostCap(cap: CostCap): Promise<CostCap> {
  return (await call<{ cap: CostCap }>("/admin/cost-cap", "POST", { cap })).cap;
}

export async function getCostUsage(patientId: string): Promise<CostUsage> {
  const q = new URLSearchParams({ patientId });
  return call<CostUsage>(`/admin/cost-usage?${q}`, "GET");
}

export interface CostReportRow {
  modelKey: string;
  context: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  requestCount: number;
}

export interface PatientCostReport {
  patientId: string;
  patientName: string;
  color: string;
  rows: CostReportRow[];
  totalCostUsd: number;
  totalInputTokens: number;
  totalOutputTokens: number;
}

export interface AdminCostReport {
  reports: PatientCostReport[];
  grandTotalCostUsd: number;
  grandTotalInputTokens: number;
  grandTotalOutputTokens: number;
}

export async function getAdminCostReport(): Promise<AdminCostReport> {
  return call<AdminCostReport>("/admin/cost-report", "GET");
}

export interface CostTrendRow {
  date: string; // YYYY-MM-DD
  modelKey: string;
  context: string; // BedrockContext (chat, onboard, drug-reference, …)
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  requestCount: number;
}

export interface CostTrendPatient {
  patientId: string;
  patientName: string;
  color: string;
  rows: CostTrendRow[];
}

export interface CostTrend {
  days: string[]; // ascending YYYY-MM-DD, one per day in the window
  patients: CostTrendPatient[];
}

export async function getCostTrend(days = 7): Promise<CostTrend> {
  const q = new URLSearchParams({ days: String(days) });
  return call<CostTrend>(`/admin/cost-trend?${q}`, "GET");
}

// ---- Analysis mode (PLAN.md §18) ----

/** Streams an analysis run. Re-uses the same STREAM_URL as capture; pass mode="analysis". */
export async function streamAnalysis(
  patientId: string,
  bundle: string,
  promptText: string,
  cb: Omit<StreamCallbacks, "onProposal" | "onSession" | "onDone"> & {
    onDone: (meta: { inputTokens?: number; outputTokens?: number }) => void;
  }
): Promise<void> {
  if (!STREAM_URL) throw new ApiError("Streaming is not configured", 0);
  const token = getToken();
  if (!token) throw new ApiError("Not signed in", 401);

  debugLog("info", "→ STREAM /analysis");
  let res: Response;
  try {
    res = await fetch(STREAM_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ patientId, bundle, promptText, mode: "analysis" }),
    });
  } catch (networkErr) {
    const msg = networkErr instanceof Error ? networkErr.message : String(networkErr);
    debugLog("error", `✗ STREAM /analysis — network error: ${msg}`, { url: STREAM_URL });
    throw new ApiError(`Network error streaming analysis: ${msg}`, 0);
  }
  if (res.status === 401) {
    clearToken();
    throw new ApiError("Session expired — please sign in again", 401);
  }
  if (!res.ok || !res.body) {
    debugLog("error", `✗ STREAM /analysis — ${res.status}`);
    throw new ApiError(`Analysis stream failed (${res.status})`, res.status);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buf.indexOf("\n\n")) >= 0) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      let evt: Record<string, unknown>;
      try {
        evt = JSON.parse(dataLine.slice(5).trim());
      } catch {
        continue;
      }
      switch (evt.type) {
        case "delta":
          cb.onDelta(evt.text as string);
          break;
        case "debug": {
          const d = evt as unknown as BedrockDebug;
          recordBedrockInteraction({
            mode: d.mode,
            modelId: d.modelId,
            request: d.request,
            responseText: d.responseText,
            toolUses: d.toolUses,
            inputTokens: d.inputTokens,
            outputTokens: d.outputTokens,
            durationMs: d.durationMs,
            fellBackFrom: d.fellBackFrom,
          });
          break;
        }
        case "done":
          cb.onDone({ inputTokens: evt.inputTokens as number | undefined, outputTokens: evt.outputTokens as number | undefined });
          break;
        case "error":
          cb.onError(
            evt.detail ? `${evt.message as string}: ${evt.detail as string}` : (evt.message as string)
          );
          break;
      }
    }
  }
  debugLog("info", "✓ STREAM /analysis — closed");
}

export interface AnalysisSlice {
  rangeStart: string;
  rangeEnd: string;
  eventTypes: string[];
  eventCount: number;
}

export async function saveAnalysisReport(
  patientId: string,
  promptText: string,
  fullText: string,
  slice: AnalysisSlice,
  modelId: string,
  tokens: { input: number; output: number },
  durationMs: number
): Promise<{ reportId: string; occurredAt: string }> {
  return call<{ reportId: string; occurredAt: string }>("/analysis/reports/save", "POST", {
    patientId,
    promptText,
    fullText,
    slice,
    modelId,
    tokens,
    durationMs,
  });
}

export interface AnalysisReportContent {
  markdown: string;
  prompt: string;
  slice: AnalysisSlice;
  model: { id: string };
  tokens?: { input: number; output: number };
}

export async function getAnalysisReportContent(
  patientId: string,
  reportId: string
): Promise<AnalysisReportContent> {
  const q = new URLSearchParams({ patientId, reportId });
  return call<AnalysisReportContent>(`/analysis/reports/content?${q}`, "GET");
}

// ---- Pain analysis (PAIN_TRACKING_PLAN.md Task 7.3, D-PAIN-11 / D-PAIN-14) ----

export interface PainAnalysisResponse {
  windowDays: number;
  correlations: PainCorrelation[];
  /** Hedged markdown narrative, or "" when the model call failed. */
  narrative: string;
  /** Set when the deterministic correlations came back but the model call did not. */
  narrativeError?: string;
  modelId: string;
  tokens: { input: number; output: number };
  costUsd: number;
  eventCount: number;
  generatedAt: string;
}

/** Run a non-streaming pain analysis over a trailing window (7 / 30 / 90 / 365 days). */
export async function runPainAnalysis(
  patientId: string,
  windowDays: number
): Promise<PainAnalysisResponse> {
  return call<PainAnalysisResponse>("/pain/analysis", "POST", { patientId, windowDays });
}

// ── Apple Health import (§21.1) ───────────────────────────────────────────────

export interface IngestStatus {
  patientId: string;
  uploadId: string;
  status: string;
  totalRecords: number;
  importedRecords: number;
  skippedRecords: number;
  errorRecords: number;
  errorMessage?: string;
  startedAt: string;
  completedAt?: string;
}

export async function getAppleHealthUploadUrl(
  patientId: string
): Promise<{ uploadUrl: string; uploadId: string; key: string }> {
  return call(`/ingest/applehealth/upload-url?patientId=${encodeURIComponent(patientId)}`, "GET");
}

export async function getAppleHealthStatus(
  patientId: string,
  uploadId: string
): Promise<{ status: IngestStatus }> {
  const q = new URLSearchParams({ patientId, uploadId });
  return call(`/ingest/applehealth/status?${q}`, "GET");
}

export async function getAppleHealthHistory(
  patientId: string
): Promise<{ history: IngestStatus[] }> {
  return call(`/ingest/applehealth/history?patientId=${encodeURIComponent(patientId)}`, "GET");
}

export async function getAppleHealthToken(
  patientId: string
): Promise<{ token: string; webhookUrl: string; patientId: string }> {
  return call(`/ingest/applehealth/token?patientId=${encodeURIComponent(patientId)}`, "GET");
}

export async function rotateAppleHealthToken(
  patientId: string
): Promise<{ token: string; webhookUrl: string; patientId: string }> {
  return call("/ingest/applehealth/token/rotate", "POST", { patientId });
}

// ── Patient location + weather opt-in (§21.5) ────────────────────────────────

export async function savePatientLocation(
  patientId: string,
  location: {
    locationLat?: number;
    locationLon?: number;
    locationCity?: string;
    weatherEnrichmentEnabled?: boolean;
  }
): Promise<{ patient: Patient }> {
  return call("/patients/location", "POST", { patientId, ...location });
}

// ── Medication review reminder ────────────────────────────────────────────────

export async function markMedicationsReviewed(patientId: string): Promise<{ patient: Patient }> {
  return call("/patients/medication-review", "POST", { patientId });
}

// ── Drug interaction check (MEDICATION_INTERACTIONS_IMPLEMENTATION.md Phase 3) ──

export interface InteractionCheckResponse {
  warnings: import("@medassist/shared").InteractionWarning[];
  checkedAt: string;
  fallbackUsed: boolean;
  rxnavUnreachable: boolean;
  /** True when interaction detection could not complete — distinguishes a real
   *  "none found" from a failed check so the UI never falsely reassures. */
  interactionsUnavailable?: boolean;
  unresolved: string[];
  fromCache?: boolean;
}

/**
 * Checks drug interactions via the backend endpoint.
 * - `drugNames` omitted → comprehensive review against all regimen meds (cached).
 * - `drugNames` provided → ad-hoc check (chat path / explicit list), no cache write.
 * - `focusNames` provided → restrict warnings to pairs touching one of these
 *   (the meds being marked taken); keeps the chat modal in step with the inline
 *   warnings. Ignored on the comprehensive path.
 * - `force = true` → bypass the server-side review cache.
 */
export async function checkDrugInteractions(
  patientId: string,
  drugNames?: string[],
  force?: boolean,
  focusNames?: string[]
): Promise<InteractionCheckResponse> {
  return call<InteractionCheckResponse>("/patients/interactions/check", "POST", {
    patientId,
    ...(drugNames !== undefined ? { drugNames } : {}),
    ...(focusNames !== undefined ? { focusNames } : {}),
    ...(force ? { force: true } : {}),
  });
}

// ── Drug reference + active levels (MEDICATION_LEVELS_PLAN.md) ────────────────

/**
 * Estimate how much of each logged medication is still in the patient's system,
 * based on logged doses and each drug's half-life (server-side decay model).
 */
export async function getActiveDrugLevels(
  patientId: string
): Promise<import("@medassist/shared").ActiveLevelsResponse> {
  const q = new URLSearchParams({ patientId });
  // Derived from regimen meds + logged doses; both change only via writes (which clear
  // the cache), and decay over the TTL is negligible — safe to cache (PERFORMANCE_PLAN.md).
  return call(`/patients/medications/active-levels?${q}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list });
}

/**
 * Fetch active ingredients shared by two or more of the patient's medications
 * (cumulative-dose risk, e.g. acetaminophen in both Tylenol and Excedrin).
 */
export async function getIngredientOverlaps(
  patientId: string
): Promise<import("@medassist/shared").IngredientOverlapsResponse> {
  const q = new URLSearchParams({ patientId });
  // Pure function of the patient's regimen meds → safe to cache (PERFORMANCE_PLAN.md).
  return call(`/patients/medications/ingredient-overlaps?${q}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list });
}

/** Fetch concise dosage/half-life reference data for a single drug name. */
export async function getDrugReference(
  name: string,
  patientId?: string
): Promise<import("@medassist/shared").DrugReference> {
  const q = new URLSearchParams({ name });
  if (patientId) q.set("patientId", patientId);
  // Drug reference data (half-life, dosage) is intrinsic to the drug, not a moment-in-time
  // patient fact — safe to cache like the other medication-detail reads (PERFORMANCE_PLAN.md #5).
  // Callers that fan out over many historical med names (MedicationSystemCalendar) previously
  // re-fetched every name on every mount; this was the #1 call volume in the Perf tab.
  return (
    await call<{ reference: import("@medassist/shared").DrugReference }>(
      `/drugs/reference?${q}`,
      "GET",
      undefined,
      true,
      { cacheTtlMs: CACHE_TTL.list }
    )
  ).reference;
}

// ── Food guidance (MEDICATION_FOOD_GUIDANCE_PLAN.md) ─────────────────────────

/** Fetch food/drink guidance for a single drug name. */
export async function getFoodGuidance(
  name: string,
  patientId?: string
): Promise<import("@medassist/shared").FoodGuidance> {
  const q = new URLSearchParams({ name });
  if (patientId) q.set("patientId", patientId);
  // Same rationale as getDrugReference above: intrinsic to the drug, safe to cache.
  return (
    await call<{ guidance: import("@medassist/shared").FoodGuidance }>(
      `/drugs/food-guidance?${q}`,
      "GET",
      undefined,
      true,
      { cacheTtlMs: CACHE_TTL.list }
    )
  ).guidance;
}

/** Fetch food/drink guidance for all active regimen meds (powers the per-med badge). */
export async function getMedicationFoodGuidance(
  patientId: string
): Promise<{ items: { name: string; guidance: import("@medassist/shared").FoodGuidance }[]; computedAt: string }> {
  const q = new URLSearchParams({ patientId });
  // Derived from the patient's regimen meds → safe to cache (PERFORMANCE_PLAN.md).
  return call(`/patients/medications/food-guidance?${q}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list });
}

/** Fetch symptom <-> medication-side-effect matches for the Reports tab's 5th
 *  report (MEDICATION_SIDE_EFFECT_WARNING_PLAN.md). */
export async function getSideEffectMatches(
  patientId: string,
  days: number
): Promise<{ matches: SideEffectSymptomMatch[]; computedAt: string; windowDays: number }> {
  const q = new URLSearchParams({ patientId, days: String(days) });
  return call(`/patients/medications/side-effect-matches?${q}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list });
}

// ── CSV import (§22.3) ────────────────────────────────────────────────────────

export interface CsvEventInput {
  eventType: string;
  occurredAt?: string;
  payload: Record<string, unknown>;
  externalId?: string;
}

export async function importCsvEvents(
  patientId: string,
  events: CsvEventInput[]
): Promise<{ saved: number; skipped: number; errors?: string[] }> {
  return call("/import/csv", "POST", { patientId, events });
}

// ── Document ingest: PDF / image OCR (§22.1, §22.4, §22.5) ──────────────────

export type ImportJobStatus = "queued" | "processing" | "done" | "error";
export type AnalysisMode = "text" | "structured";

export interface ImportJob {
  jobId: string;
  patientId: string;
  source: string;
  fileName: string;
  mimeType: string;
  s3Key: string;
  analysisMode: AnalysisMode;
  status: ImportJobStatus;
  candidateCount: number;
  approvedCount: number;
  rejectedCount: number;
  createdAt: string;
  processedAt?: string;
  errorMessage?: string;
}

export interface ImportCandidate {
  candidateId: string;
  jobId: string;
  patientId: string;
  eventType: string;
  payload: Record<string, unknown>;
  occurredAt: string;
  confidence: "high" | "medium" | "low";
  notes?: string;
  sourceRefs: string[];
  status: "needs_review" | "approved" | "rejected";
}

/**
 * Generates a presigned S3 PUT URL and creates a queued import job.
 * The caller uploads the file directly to S3 at uploadUrl, then polls the job.
 */
export async function getDocumentUploadUrl(
  patientId: string,
  fileName: string,
  mimeType: string,
  analysisMode: AnalysisMode = "text"
): Promise<{ uploadUrl: string; jobId: string; s3Key: string }> {
  const q = new URLSearchParams({ patientId, fileName, mimeType, analysisMode });
  return call(`/ingest/document/upload-url?${q}`, "GET");
}

/** Lists all document import jobs for a patient, newest first. */
export async function listDocumentJobs(
  patientId: string
): Promise<{ jobs: ImportJob[] }> {
  return call(`/ingest/document/jobs?patientId=${encodeURIComponent(patientId)}`, "GET");
}

/** Lists all extraction candidates for an import job. */
export async function listDocumentCandidates(
  patientId: string,
  jobId: string
): Promise<{ candidates: ImportCandidate[] }> {
  const q = new URLSearchParams({ patientId, jobId });
  return call(`/ingest/document/candidates?${q}`, "GET");
}

/** Approves selected candidates — creates active MedicalEvent records from them. */
export async function approveDocumentCandidates(
  patientId: string,
  jobId: string,
  candidateIds: string[]
): Promise<{ approved: number }> {
  return call("/ingest/document/approve", "POST", { patientId, jobId, candidateIds });
}

/** Rejects selected candidates — marks them rejected (kept for audit). */
export async function rejectDocumentCandidates(
  patientId: string,
  jobId: string,
  candidateIds: string[]
): Promise<{ rejected: number }> {
  return call("/ingest/document/reject", "POST", { patientId, jobId, candidateIds });
}

/**
 * Uploads a file directly to S3 using a presigned PUT URL.
 * Returns when the upload is complete.
 */
export async function uploadFileToS3(
  presignedUrl: string,
  file: File,
  onProgress?: (pct: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", file.type);
    // Without a timeout, a stalled mobile connection (gym wifi/cellular) leaves this
    // promise pending forever — the caller's "Uploading…" state never clears and
    // never surfaces an error, looking like a silent hang rather than a failure.
    xhr.timeout = 45000;
    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed: ${xhr.status}`));
    });
    xhr.addEventListener("error", () => reject(new Error("S3 upload network error")));
    xhr.addEventListener("timeout", () => reject(new Error("S3 upload timed out — check your connection and try again")));
    xhr.send(file);
  });
}

// ── PT sheet scan (PT_EXERCISE_SCAN_PLAN.md Phase 5) ─────────────────────────

import type { CustomPtExercise } from "@medassist/shared";

export type { CustomPtExercise };

export interface PtSheetJob {
  jobId: string;
  status: "queued" | "processing" | "done" | "failed";
  candidateCount?: number;
  error?: string;
  exercises?: CustomPtExercise[];
}

/** Get a presigned S3 PUT URL and create a queued PT-sheet ingest job. */
export async function getPtSheetUploadUrl(
  patientId: string,
  fileName: string,
  mimeType: string
): Promise<{ uploadUrl: string; jobId: string; s3Key: string }> {
  const q = new URLSearchParams({ patientId, fileName, mimeType });
  return call(`/pt/sheet/upload-url?${q}`, "GET");
}

/** Get a presigned S3 PUT URL for an additional PT sheet page (page 2+). */
export async function getPtSheetPageUrl(
  patientId: string,
  jobId: string,
  fileName: string,
  mimeType: string,
  pageIndex: number
): Promise<{ uploadUrl: string; s3Key: string }> {
  const q = new URLSearchParams({ patientId, jobId, fileName, mimeType, pageIndex: String(pageIndex) });
  return call(`/pt/sheet/page-url?${q}`, "GET");
}

/** Poll a PT-sheet ingest job. When status is "done", exercises are included. */
export async function getPtSheetJob(
  patientId: string,
  jobId: string
): Promise<PtSheetJob> {
  const q = new URLSearchParams({ patientId, jobId });
  // The API responds with { job: ImportJob, exercises } — flatten it into the
  // PtSheetJob shape the UI expects, and normalize the "error" status to "failed".
  const res = await call<{
    job: { jobId: string; status: string; candidateCount?: number; errorMessage?: string };
    exercises?: CustomPtExercise[];
  }>(`/pt/sheet/job?${q}`, "GET");
  const rawStatus = res.job?.status;
  const status: PtSheetJob["status"] =
    rawStatus === "error" ? "failed" : (rawStatus as PtSheetJob["status"]);
  return {
    jobId: res.job?.jobId ?? jobId,
    status,
    candidateCount: res.job?.candidateCount,
    error: res.job?.errorMessage,
    exercises: res.exercises,
  };
}

/** Get a short-lived presigned GET URL for viewing a PT sheet page image. */
export async function getPtSheetImageUrl(patientId: string, s3Key: string): Promise<string> {
  const q = new URLSearchParams({ patientId, s3Key });
  const res = await call<{ url: string }>(`/pt/sheet/image?${q}`, "GET");
  return res.url;
}

/** Save a scanned PT program (creates a pt_program event with source:"scanned"). */
export async function savePtProgram(payload: {
  patientId: string;
  customExercises: CustomPtExercise[];
  cadence: string;
  sheetTitle?: string;
  sourceImageRef?: string;
}): Promise<{ eventId: string }> {
  return call("/pt/program", "POST", payload);
}

/** Set or clear the archived flag on a pt_program event. */
export async function setPtProgramArchived(payload: {
  patientId: string;
  eventId: string;
  occurredAt: string;
  archived: boolean;
}): Promise<void> {
  await call("/pt/program/archive", "POST", payload);
}

/** Update customExercises / sheetTitle / cadence on an existing scanned pt_program. */
export async function updatePtProgram(payload: {
  patientId: string;
  eventId: string;
  occurredAt: string;
  customExercises: CustomPtExercise[];
  sheetTitle?: string;
  cadence?: string;
}): Promise<void> {
  await call("/pt/program", "PATCH", payload);
}

/** Run Bedrock muscle-group analysis on scanned PT exercises (PT_MUSCLE_BALANCE_PLAN Phase 1).
 *  Omit `exerciseIds` to analyze every exercise in the program that has never been analyzed.
 *  Returns the full updated customExercises array so the caller can setState directly. */
export async function analyzePtExerciseMuscles(payload: {
  patientId: string;
  eventId: string;
  occurredAt: string;
  exerciseIds?: string[];
}): Promise<{ ok: true; analyzed: number; customExercises: CustomPtExercise[] }> {
  return call("/pt/exercise/muscles", "POST", payload);
}

// ── Onboarding V2: Medication-label photo OCR (§25.4, D-58) ──────────────────

/** Extracted fields from a medication bottle label. All fields optional (OCR may miss some). */
export interface MedLabelResult {
  name?: string;
  dose?: number;
  doseUnit?: string;
  route?: string;
  scheduledGroup?: string;
  frequency?: string;
  instructions?: string;
  rawText?: string;
  confidence: "high" | "medium" | "low";
}

/** Get a presigned S3 PUT URL to upload a medication bottle photo. */
export async function getMedLabelUploadUrl(
  patientId: string,
  fileName: string,
  mimeType: string
): Promise<{ uploadUrl: string; s3Key: string; jobId: string }> {
  const q = new URLSearchParams({ patientId, fileName, mimeType });
  return call(`/ingest/medlabel/upload-url?${q}`, "GET");
}

/**
 * Ask the API to read the uploaded med-bottle photo with Bedrock vision.
 * Returns extracted label fields (name, dose, route, etc.) for review.
 */
export async function extractMedLabel(
  patientId: string,
  s3Key: string
): Promise<MedLabelResult> {
  return call("/ingest/medlabel/extract", "POST", { patientId, s3Key });
}

// ── Food tracking: meal-photo vision + USDA nutrition (FOOD_TRACKING_PLAN.md) ──

/** The editable review payload returned by the meal-photo extract. */
export interface FoodExtractResult {
  items: FoodItemBreakdown[];
  totals: NutrientTotals;
  mealType?: MealType;
  notes?: string;
  confidence: "high" | "medium" | "low";
  photoS3Key: string;
}

/** Presigned PUT URLs for the original meal photo + its client-made thumbnail. */
export async function getFoodPhotoUploadUrl(
  patientId: string,
  fileName: string,
  mimeType: string
): Promise<{ uploadUrl: string; thumbUploadUrl: string; s3Key: string; thumbS3Key: string; jobId: string }> {
  const q = new URLSearchParams({ patientId, fileName, mimeType });
  return call(`/ingest/foodphoto/upload-url?${q}`, "GET");
}

/** Run Bedrock vision over the uploaded meal photo and ground it in USDA nutrition. */
export async function extractFoodPhoto(patientId: string, s3Key: string): Promise<FoodExtractResult> {
  return call("/ingest/foodphoto/extract", "POST", { patientId, s3Key });
}

/** USDA search candidates for the swap-match box (per-100g nutrition each). */
export async function searchNutrition(patientId: string, q: string): Promise<FoodNutritionPer100g[]> {
  const qs = new URLSearchParams({ patientId, q });
  return (await call<{ candidates: FoodNutritionPer100g[] }>(`/nutrition/search?${qs}`, "GET")).candidates;
}

/** Fetch a single USDA food's per-100g nutrition by fdcId. */
export async function getNutritionFood(patientId: string, fdcId: number): Promise<FoodNutritionPer100g> {
  const qs = new URLSearchParams({ patientId, fdcId: String(fdcId) });
  return (await call<{ food: FoodNutritionPer100g }>(`/nutrition/food?${qs}`, "GET")).food;
}

/** Presigned GET URL to display a retained meal photo / thumbnail. */
export async function getFoodPhotoViewUrl(patientId: string, s3Key: string): Promise<string> {
  const qs = new URLSearchParams({ patientId, s3Key });
  return (await call<{ url: string }>(`/foodphoto/view-url?${qs}`, "GET")).url;
}

// ── Food catalog (NUTRITION_AND_SUBSTANCE_LEVELS_PLAN.md D-NUT-5) ──

export async function listFoodCatalog(patientId: string): Promise<FoodCatalogView[]> {
  const q = new URLSearchParams({ patientId });
  return (await call<{ items: FoodCatalogView[] }>(`/food/catalog?${q}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list })).items;
}

export async function createFoodCatalogItem(body: {
  patientId: string;
  name: string;
  fdcId?: number;
  matchName?: string;
  per100g?: FoodNutritionPer100g;
  defaultQuantity?: number;
  defaultUnit?: string;
  defaultGrams?: number;
  source?: UserFoodItem["source"];
  photoS3Key?: string;
  thumbS3Key?: string;
  barcode?: string;
  isBeverage?: boolean;
  fluidType?: FluidType;
  defaultVolumeMl?: number;
}): Promise<{ item: UserFoodItem; existing: boolean }> {
  return call("/food/catalog", "POST", body);
}

export async function updateFoodCatalogItem(
  userFoodId: string,
  body: {
    patientId: string;
    name?: string;
    defaultQuantity?: number;
    defaultUnit?: string;
    defaultGrams?: number;
    per100g?: FoodNutritionPer100g;
    fdcId?: number;
    matchName?: string;
  }
): Promise<UserFoodItem> {
  return (await call<{ item: UserFoodItem }>(`/food/catalog/${encodeURIComponent(userFoodId)}`, "PATCH", body)).item;
}

export async function deleteFoodCatalogItem(
  patientId: string,
  userFoodId: string,
  ownerPatientId?: string
): Promise<void> {
  const q = new URLSearchParams({ patientId });
  if (ownerPatientId) q.set("ownerPatientId", ownerPatientId);
  await call<{ ok: boolean }>(`/food/catalog/${encodeURIComponent(userFoodId)}?${q}`, "DELETE");
}

export type FoodCatalogDraft = Omit<
  UserFoodItem,
  "userFoodId" | "ownerPatientId" | "createdAt" | "updatedAt"
>;

export async function identifyFoodCatalog(body: {
  patientId: string;
  s3Key?: string;
  thumbS3Key?: string;
  description?: string;
}): Promise<{ draft: FoodCatalogDraft | null; error?: string }> {
  return call("/food/catalog/identify", "POST", body);
}

export async function logFoodCatalog(body: {
  patientId: string;
  mealType?: string;
  eatenAt?: string;
  notes?: string;
  entries: Array<{
    userFoodId: string;
    ownerPatientId?: string;
    quantity?: number;
    unit?: string;
    grams?: number;
  }>;
}): Promise<MedicalEvent> {
  return (await call<{ event: MedicalEvent }>("/food/catalog/log", "POST", body)).event;
}

/** GET /nutrition/barcode?code= — 200 with `product: null` on miss (never 404). */
export async function lookupNutritionBarcode(code: string): Promise<{ product: BarcodeProduct | null }> {
  const qs = new URLSearchParams({ code });
  return call(`/nutrition/barcode?${qs}`, "GET");
}

export interface LogIntakeBody {
  patientId: string;
  asBeverage: boolean;
  quantity?: number;
  unit?: string;
  grams?: number;
  volumeMl?: number;
  fluidType?: FluidType;
  portion?: number;
  caffeineMgOverride?: number;
  caffeineMgPerServing?: number;
  userFoodId?: string;
  ownerPatientId?: string;
  product?: BarcodeProduct;
  mealType?: MealType;
  eatenAt?: string;
  notes?: string;
}

export async function logIntake(body: LogIntakeBody): Promise<{
  food: { eventId: string; occurredAt: string };
  hydration?: { eventId: string; occurredAt: string };
  caffeineSaved?: boolean;
}> {
  return call("/food/catalog/log-intake", "POST", body);
}

// ── Nutrient report (NUTRITION_AND_SUBSTANCE_LEVELS_PLAN.md D-NUT-8) ──

export async function getNutritionSummary(patientId: string, rangeDays: number): Promise<NutrientReport> {
  const q = new URLSearchParams({ patientId, rangeDays: String(rangeDays) });
  return call<NutrientReport>(`/patients/nutrition/summary?${q}`, "GET");
}

export async function getNutritionNarrative(body: {
  patientId: string;
  rangeDays: number;
  daysWithData: number;
  topDeficits: NutrientGap[];
  topExcesses: NutrientGap[];
}): Promise<{ narrative: string | null }> {
  return call("/patients/nutrition/narrative", "POST", body);
}

// ── Body-progress photos: vision body-composition (BODY_COMPOSITION_PLAN.md) ──

/** A slot in a body-progress capture: an angle, or the blurred thumbnail. */
export type BodyPhotoSlot = BodyPhotoAngle | "thumb";

/** Presigned PUT URL for one body-photo slot. Pass the same `photoId` for every
 *  slot of one capture (omit on the first call; reuse the returned id thereafter). */
export async function getBodyPhotoUploadUrl(
  patientId: string,
  slot: BodyPhotoSlot,
  fileName: string,
  mimeType: string,
  photoId?: string
): Promise<{ uploadUrl: string; s3Key: string; photoId: string }> {
  const q = new URLSearchParams({ patientId, slot, fileName, mimeType, ...(photoId ? { photoId } : {}) });
  return call(`/ingest/bodyphoto/upload-url?${q}`, "GET");
}

/** The editable proposal returned by the body-photo extract (nothing saved yet). */
export interface BodyExtractResult {
  proposal: BodyCompositionPayload;
  warnings: string[];
}

/** Run Bedrock vision over the uploaded body photo(s); returns an editable proposal.
 *  `manual` entries (scale weight / tape measurements) override vision estimates. */
export interface BodyPhotoContext {
  demographics?: {
    sex?: string;
    ageYears?: number;
    heightCm?: number;
    weightKg?: number;
  };
  priorScans?: Array<{
    measuredAt?: string;
    confirmedMetrics?: Record<string, { value: number; unit?: string }>;
    aiEstimates?: Record<string, { value: number; unit?: string; confidence?: number }>;
    bodyShapeClass?: string;
    stableObservations?: Record<string, string>;
  }>;
  /** Most recent tape-measured session. Ground truth — outranks any photo estimate
   *  (BODY_MEASUREMENTS_PLAN.md Phase 7, D-MEAS-15). */
  tapeMeasurements?: {
    measuredAt: string;
    ageDays: number;
    sites: Record<string, { cm: number }>;
    conditions?: { timeOfDay?: string; fasted?: boolean; postWorkout?: boolean };
  };
  /** Newest logged weight event; more current than demographics.weightKg. */
  recentWeight?: { kg: number; measuredAt: string };
  trainingContext?: { windowDays: number; resistanceDaysPerWeek: number; topTrainedMuscles: string[] };
  nutritionContext?: { windowDays: number; avgDailyKcal: number; avgProteinG: number; estimatedTdeeKcal: number };
  hydration24hMl?: number;
  fluidRetentionMeds?: string[];
  cyclePhase?: string;
  photoTimeOfDay?: "morning" | "midday" | "evening";
}

export async function extractBodyPhoto(
  patientId: string,
  photos: { angle: BodyPhotoAngle; s3Key: string }[],
  opts?: { thumbS3Key?: string; heightCm?: number; manual?: { id: string; value: number; unit?: string }[]; context?: BodyPhotoContext; containsNudity?: boolean }
): Promise<BodyExtractResult> {
  return call("/ingest/bodyphoto/extract", "POST", { patientId, photos, ...opts });
}

/** Short-lived presigned GET URL to display a retained body photo / thumbnail (D-BC-5). */
export async function getBodyPhotoViewUrl(patientId: string, s3Key: string): Promise<string> {
  const qs = new URLSearchParams({ patientId, s3Key });
  return (await call<{ url: string }>(`/bodyphoto/view-url?${qs}`, "GET")).url;
}

export interface BodyCompositionPatch {
  metrics?: Record<string, { value: number; unit?: string; source?: string }>;
  heightCm?: number | null;
  bodyShapeClass?: string | null;
  notes?: string | null;
  aiEstimates?: Record<string, { value: number; unit?: string; confidence?: number }>;
  aiBodyShapeClass?: string;
  aiObservations?: Record<string, string>;
}

/** Merge user corrections into a saved body_composition event. */
export async function updateBodyCompositionEvent(
  patientId: string,
  eventId: string,
  occurredAt: string,
  patch: BodyCompositionPatch,
): Promise<void> {
  await call("/events/body-composition", "PATCH", { patientId, eventId, occurredAt, patch });
}

/** Re-export for pages building manual-metric edits without importing shared directly. */
export type { BodyCompositionPayload, BodyMetric };

// ── Body measurements (BODY_MEASUREMENTS_PLAN.md Phase 3) ────────────────────
/** Re-export so measurement components don't need a second import from shared. */
export type { BodyMeasurementPayload, MeasurementConditions, MeasurementDisplayUnit, CustomMeasurementSite };

export interface MeasurementSiteInput {
  id: string;
  value: number;
  unit?: MeasurementDisplayUnit;
  note?: string;
}

export interface SaveMeasurementSessionInput {
  measuredAt?: string;
  unit: MeasurementDisplayUnit;
  sites: MeasurementSiteInput[];
  conditions?: MeasurementConditions;
  heightCmAtTime?: number;
  weightKgAtTime?: number;
  notes?: string;
}

export interface SaveMeasurementSessionResult {
  event: MedicalEvent;
  warnings: string[];
}

/** Direct-save one body_measurement session — a form submit is its own
 *  confirmation, no Bedrock proposal involved. */
export async function saveMeasurementSession(
  patientId: string,
  input: SaveMeasurementSessionInput,
): Promise<SaveMeasurementSessionResult> {
  return call("/measurements", "POST", { patientId, ...input });
}

export interface PatchMeasurementSessionInput {
  /** Required by the server validator even when `sites` is unchanged. */
  unit: MeasurementDisplayUnit;
  sites?: MeasurementSiteInput[];
  conditions?: MeasurementConditions | null;
  heightCmAtTime?: number | null;
  weightKgAtTime?: number | null;
  notes?: string | null;
}

/** Re-validate and overwrite a saved body_measurement session's sites/
 *  conditions/notes in place. */
export async function patchMeasurementSession(
  patientId: string,
  eventId: string,
  occurredAt: string,
  input: PatchMeasurementSessionInput,
): Promise<{ warnings: string[] }> {
  return call("/events/body-measurement", "PATCH", { patientId, eventId, occurredAt, ...input });
}

/** Attach (or replace) the pinpoint bodyMap on a saved injury event
 *  (PAIN_TRACKING_PLAN.md Task 6.2, D-PAIN-9). `occurredAt` locates the event's
 *  composite key — pass it from the `MedicalEvent` already in hand. */
export async function patchInjuryBodyMap(
  patientId: string,
  eventId: string,
  occurredAt: string,
  bodyMap: PainBodyMap,
): Promise<void> {
  await call("/events/injury-bodymap", "PATCH", { patientId, eventId, occurredAt, bodyMap });
}

/** Edit profile fields + nutrition targets (FOOD_TRACKING_PLAN.md D-FOOD-8/10/11). */
export interface PatientProfilePatch {
  dob?: string;
  sex?: "female" | "male" | "intersex" | "unspecified";
  heightCm?: number;
  activityLevel?: "sedentary" | "light" | "moderate" | "active";
  dailyCalorieGoal?: number;
  macroTargets?: { carbsG?: number; proteinG?: number; fatG?: number };
  weightKg?: number;
  preferredWeightUnit?: "lbs" | "kg";
  preferredMeasurementUnit?: MeasurementDisplayUnit;
  /** Appends/replaces the patient's custom measurement sites (server caps at
   *  MAX_CUSTOM_MEASUREMENT_SITES). Used by MeasurementImportModal's
   *  "create a custom site" mapping option (BODY_MEASUREMENTS_PLAN.md Task 5.4). */
  customMeasurementSites?: CustomMeasurementSite[];
  /** Clamped [7, 180] server-side. Days without a measuring session before the
   *  catch-up card appears (BODY_MEASUREMENTS_PLAN.md D-MEAS-14). */
  measurementReminderDays?: number;
  /** Whether measurements/weight/training/nutrition/hydration/med context is sent
   *  to the body-photo vision model to calibrate its estimates. Absent = true
   *  (BODY_MEASUREMENTS_PLAN.md D-MEAS-15). */
  bodyVisionContextEnabled?: boolean;
}

export async function savePatientProfile(patientId: string, patch: PatientProfilePatch): Promise<Patient> {
  return (await call<{ patient: Patient }>("/patients/profile", "POST", { patientId, ...patch })).patient;
}

// ── Admin: User Management (§21.3) ────────────────────────────────────────────

export type CognitoGroupName = "admin" | "household" | "clinician";
export type PermissionLevel = "read" | "write" | "admin";

export interface PatientAccessEntry {
  patientId:   string;
  permission:  PermissionLevel;
}

export interface AdminUser {
  userId:          string;
  email:           string;
  displayName?:    string;
  role:            CognitoGroupName;
  approvalStatus:  "ACTIVE" | "REVOKED";
  /** Cognito account status. "FORCE_CHANGE_PASSWORD" = invited but not yet
   *  signed in (invite can be resent); "CONFIRMED" = has set their password. */
  status?:         string;
  createdAt:       string;
  lastActiveAt?:   string;
  patientAccess:   PatientAccessEntry[];
}

export async function listUsers(): Promise<AdminUser[]> {
  return (await call<{ users: AdminUser[] }>("/admin/users", "GET")).users;
}

export async function inviteUser(
  email:        string,
  role:         CognitoGroupName,
  patientAccess: PatientAccessEntry[],
  requireMfa:   boolean = false
): Promise<{ userId: string }> {
  return call<{ userId: string }>("/admin/users/invite", "POST", { email, groupName: role, patientAccess, requireMfa });
}

export async function revokeUser(userId: string, sendEmail = false): Promise<void> {
  await call(`/admin/users/${encodeURIComponent(userId)}/revoke`, "POST", { sendEmail });
}

/** Resend the temporary-password invite email to a user who hasn't signed in yet. */
export async function resendInvite(userId: string): Promise<void> {
  await call(`/admin/users/${encodeURIComponent(userId)}/resend`, "POST");
}

export async function updateUserAccess(
  userId: string,
  grant:  PatientAccessEntry[],
  revoke: string[]           // patientId list to revoke
): Promise<void> {
  await call(`/admin/users/${encodeURIComponent(userId)}/access`, "PUT", { grant, revoke });
}

// ── Data management: export + deletion (§data-management) ────────────────────

export type DeletionCategory =
  | "vital_signs" | "medications" | "labs" | "sleep" | "exercise"
  | "food_hydration" | "symptoms" | "mood_stress" | "visits"
  | "allergies" | "vaccinations" | "family_history"
  | "chat_history" | "regimens" | "uploaded_files" | "all";

export const DELETION_CATEGORY_LABELS: Record<DeletionCategory, string> = {
  vital_signs:    "Vital Signs",
  medications:    "Medications Taken",
  labs:           "Lab Results",
  sleep:          "Sleep",
  exercise:       "Exercise",
  food_hydration: "Food & Hydration",
  symptoms:       "Symptoms & Headaches",
  mood_stress:    "Mood & Stress",
  visits:         "Visits & Procedures",
  allergies:      "Allergies",
  vaccinations:   "Vaccinations",
  family_history: "Family History",
  chat_history:   "Chat History",
  regimens:       "Medication Regimens",
  uploaded_files: "Uploaded Files & Photos",
  all:            "Everything (complete wipe)",
};

export type DeletionStatus =
  | "pending_confirmation"
  | "confirmed"
  | "cancelled"
  | "executing"
  | "completed"
  | "failed";

export interface DeletionRequest {
  requestId: string;
  userId: string;
  initiatedBy: string;
  targetPatientIds: string[];
  categories: DeletionCategory[];
  status: DeletionStatus;
  requestedAt: string;
  executeAfter?: string;
  confirmedAt?: string;
  cancelledAt?: string;
  completedAt?: string;
  errorMessage?: string;
  affectedUserEmail: string;
  initiatorEmail: string;
}

/**
 * Requests a data export for the given patient and categories.
 * Returns a presigned S3 GET URL (1-hour expiry) to download the JSON file.
 */
export async function requestDataExport(
  patientId: string,
  categories: DeletionCategory[]
): Promise<{ downloadUrl: string; exportId: string; expiresIn: number }> {
  return call("/data/export", "POST", { patientId, categories });
}

/**
 * Imports a previously-exported data block back into the patient's record.
 * `data` is the `data` field from a medical-data-export-*.json file.
 * Chat history and the profile section are silently skipped on the server.
 */
export async function importPatientData(
  patientId: string,
  data: Record<string, unknown>
): Promise<{ importedEvents: number; importedRegimens: number; skipped: number }> {
  return call("/data/import", "POST", { patientId, data });
}

// ── Full-fidelity patient export / import (PATIENT_EXPORT_IMPORT_PLAN.md) ────

/** Synchronous data-only full export — a 1-hour presigned JSON download. */
export interface FullExportJsonResult {
  format: "json";
  downloadUrl: string;
  exportId: string;
  expiresIn: number;
}
/** Async full export (photos and/or analyst bundle) — poll {@link getExportStatus}. */
export interface FullExportJobResult {
  format: "zip";
  jobId: string;
  status: string;
}
export type FullExportResult = FullExportJsonResult | FullExportJobResult;

/** Poll shape for both {@link getExportStatus} and {@link getImportStatus}. */
export interface PortabilityJobStatus {
  jobId: string;
  kind: "export" | "import";
  status: "queued" | "running" | "ready" | "failed";
  downloadUrl: string | null;
  expiresIn: number | null;
  error: string | null;
  counts: Record<string, number> | null;
  /** Present once known on an import job. */
  newPatientId?: string | null;
}

/** Synchronous data-only import result (Option B — a brand-new patient). */
export interface FullImportResult {
  newPatientId: string;
  counts: Record<string, number>;
  skipped: number;
  warnings: string[];
}

/**
 * Full-fidelity export of one patient. Data-only (both flags false) returns a
 * synchronous `{ format: "json", downloadUrl }`; requesting photos or the analyst
 * bundle returns `{ format: "zip", jobId }` — poll {@link getExportStatus}.
 */
export async function requestFullExport(
  patientId: string,
  opts: { includePhotos: boolean; includeAnalystBundle: boolean }
): Promise<FullExportResult> {
  return call("/data/export", "POST", {
    patientId,
    scope: "full",
    includePhotos: opts.includePhotos,
    includeAnalystBundle: opts.includeAnalystBundle,
  });
}

/** Polls an async full-export job. */
export async function getExportStatus(jobId: string): Promise<PortabilityJobStatus> {
  return call(`/data/export/status?jobId=${encodeURIComponent(jobId)}`, "GET");
}

/** Presigned PUT target for uploading a full-export `.zip` before importing it. */
export async function getImportUploadUrl(
  fileName: string
): Promise<{ uploadUrl: string; archiveS3Key: string }> {
  return call(`/data/import/upload-url?fileName=${encodeURIComponent(fileName)}`, "GET");
}

/**
 * Full-fidelity import (Option B — always creates a NEW patient, preserving
 * original record IDs). Pass `{ envelope }` for a data-only JSON file (synchronous,
 * returns {@link FullImportResult}) or `{ archiveS3Key }` for an uploaded `.zip`
 * (async, returns `{ jobId }` — poll {@link getImportStatus}). A re-import of the
 * same file throws `ApiError` with `status === 409` unless `force` is true.
 */
export async function importFullExport(
  body: { envelope: PatientExportEnvelope } | { archiveS3Key: string },
  force = false
): Promise<FullImportResult | { jobId: string; status: string }> {
  return call("/data/import", "POST", { ...body, force });
}

/** Polls an async `.zip` import job. */
export async function getImportStatus(jobId: string): Promise<PortabilityJobStatus> {
  return call(`/data/import/status?jobId=${encodeURIComponent(jobId)}`, "GET");
}

/**
 * Initiates a queued deletion request. A confirmation email is sent to the
 * affected user. The request must be confirmed via email before deletion is queued.
 * Admins may pass targetUserId to initiate on behalf of another user.
 */
export async function requestDataDeletion(
  targetPatientIds: string[],
  categories: DeletionCategory[],
  targetUserId?: string
): Promise<{ requestId: string; message: string }> {
  return call("/data/delete-request", "POST", { targetPatientIds, categories, targetUserId });
}

/**
 * Confirms a deletion request via the email-link token.
 * Sets executeAfter = now + 7 days, starting the cancellation window.
 */
export async function confirmDataDeletion(
  userId: string,
  requestId: string,
  token: string
): Promise<{ executeAfter: string; message: string }> {
  return call("/data/delete-confirm", "POST", { userId, requestId, token });
}

/** Cancels a deletion request (authenticated — for the in-app cancel button). */
export async function cancelDataDeletion(
  requestId: string,
  targetUserId?: string
): Promise<{ message: string }> {
  return call("/data/delete-cancel", "POST", { requestId, targetUserId });
}

/**
 * Cancels a deletion request via the email-link token (unauthenticated).
 * Used when the user clicks the cancel link from their email.
 */
export async function cancelDataDeletionByToken(
  userId: string,
  requestId: string,
  token: string
): Promise<{ message: string }> {
  return call("/data/delete-cancel-token", "POST", { userId, requestId, token }, false);
}

/** Lists all deletion requests for the current user (or another user if admin). */
export async function listDeletionRequests(
  userId?: string
): Promise<{ requests: DeletionRequest[] }> {
  const q = userId ? `?userId=${encodeURIComponent(userId)}` : "";
  return call(`/data/deletion-status${q}`, "GET");
}

// ---- Feedback (FEEDBACK_FEATURE_PLAN.md §7) ----

export interface FeedbackSubmitRequest {
  patientId: string;
  sessionId: string;
  mode: "capture" | "onboarding";
  rating: FeedbackRating;
  reasonCategory?: FeedbackCategory;
  reasonText?: string;
  usageTurns?: FeedbackUsageTurn[];
  pendingProposal?: unknown;
  /** Which UI control this rating was submitted from, e.g. "chat_footer" or
   *  "suggestion_card" — lets triage tell a context-free rating from one with a
   *  full pendingProposal, and helps steer future feedback UX fixes. */
  sourceSurface?: string;
  /** Browser timezone, used server-side to recompute the live medication-suggestion
   *  engine state (suggestionDebug) in the patient's local time. */
  timezone?: string;
}

/** Submit or update a conversation rating. Returns feedbackId and triage status. */
export async function submitFeedback(
  req: FeedbackSubmitRequest
): Promise<{ feedbackId: string; status: string | null }> {
  return call("/feedback", "POST", req);
}

/** Fetch the caller's existing rating for a session, or null if none. */
export async function getMyFeedback(
  patientId: string,
  sessionId: string
): Promise<FeedbackItem | null> {
  return call(
    `/feedback?patientId=${encodeURIComponent(patientId)}&sessionId=${encodeURIComponent(sessionId)}`,
    "GET"
  );
}

/** Admin: list ratings (newest first), optionally filtered by triage status. */
export async function adminListFeedback(opts?: {
  rating?: FeedbackRating;
  status?: FeedbackStatus;
  limit?: number;
}): Promise<FeedbackItem[]> {
  const params = new URLSearchParams();
  params.set("rating", opts?.rating ?? "down");
  if (opts?.status) params.set("status", opts.status);
  if (opts?.limit) params.set("limit", String(opts.limit));
  const res = await call<{ items: FeedbackItem[] }>(`/admin/feedback?${params.toString()}`, "GET");
  return res.items;
}

/** Admin: fetch one rating's metadata plus its immutable S3 snapshot. */
export async function adminGetFeedback(
  patientId: string,
  feedbackId: string
): Promise<{ item: FeedbackItem; snapshot: FeedbackSnapshot }> {
  return call(
    `/admin/feedback/item?patientId=${encodeURIComponent(patientId)}&feedbackId=${encodeURIComponent(feedbackId)}`,
    "GET"
  );
}

/** Admin: update a rating's triage status. */
export async function adminSetFeedbackStatus(
  patientId: string,
  feedbackId: string,
  status: FeedbackStatus
): Promise<{ ok: boolean; status: FeedbackStatus }> {
  return call("/admin/feedback/status", "POST", { patientId, feedbackId, status });
}

// ---- Condition tracking (CONDITION_TRACKING_PLAN.md Phase 2) ----

/** GET /conditions — a patient's Conditions, most recently created behavior mirrors listGymExercises. */
export async function listConditions(patientId: string, includeArchived?: boolean): Promise<Condition[]> {
  const q = new URLSearchParams({ patientId });
  if (includeArchived) q.set("includeArchived", "1");
  return (await call<{ conditions: Condition[] }>(`/conditions?${q.toString()}`, "GET", undefined, true, { cacheTtlMs: CACHE_TTL.list })).conditions;
}

/** POST /conditions — creates a Condition grouping tracked symptom/headache identities. */
export async function createCondition(
  patientId: string,
  name: string,
  trackedSymptoms: TrackedSymptom[]
): Promise<Condition> {
  return (await call<{ condition: Condition }>("/conditions", "POST", { patientId, name, trackedSymptoms })).condition;
}

/** PATCH /conditions — edit name/trackedSymptoms/archived on a Condition. */
export async function patchCondition(
  patientId: string,
  conditionId: string,
  updates: { name?: string; trackedSymptoms?: TrackedSymptom[]; archived?: boolean }
): Promise<Condition> {
  return (await call<{ condition: Condition }>("/conditions", "PATCH", { patientId, conditionId, ...updates })).condition;
}

/** POST /conditions/suggest-symptoms — on-demand AI-recommended tracked symptoms (D-COND-5, not cached). */
export async function suggestConditionSymptoms(
  patientId: string,
  conditionName: string
): Promise<{ summary: string; recommended: SymptomIdentity[] }> {
  return call("/conditions/suggest-symptoms", "POST", { patientId, conditionName });
}
